{
  "blocks": [
    {
      "id": "new-article-age-female-fertility",
      "kind": "article",
      "tags": ["moon", "moon-guides", "her", "age-factor"],
      "title": "How age shapes female fertility",
      "subtitle": "How age affects a woman's fertility",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "Age is one of the strongest predictors of female fertility. It doesn't mean you can't get pregnant after a certain birthday, but it does change the odds, the timeline, and the kind of help that may be needed. This article walks you through what actually changes with age, and what it means if you're thinking about trying naturally or using ART (IUI, IVF, egg freezing, etc.).",
        "quickSummary": [
          {
            "title": "Egg quantity declines.",
            "description": "Women are born with all eggs they'll ever have. By age 40, only ~10,000 remain from the original 300,000-400,000."
          },
          {
            "title": "Egg quality matters too.",
            "description": "Older eggs have more chromosomal errors, increasing miscarriage risk and conditions like Down syndrome."
          },
          {
            "title": "It's about probabilities.",
            "description": "Age doesn't flip fertility on or off. It changes the odds and timeline for conception."
          }
        ],
        "sections": [
          {
            "title": "How egg number and quality change",
            "subtitle": "The biology of ovarian reserve",
            "content": "Women are born with all the eggs they will ever have. The number and quality of those eggs slowly decline over time.",
            "bulletPoints": [
              "At puberty, most girls have roughly 300,000-400,000 eggs remaining in their ovaries.",
              "As time passes, thousands of eggs are lost each month. By around age 40, estimates suggest there may be ~10,000 eggs left.",
              "Egg number drops (lower ovarian reserve) while egg quality declines (more chromosomal errors, lower chance that an egg can make a healthy baby).",
              "You may still ovulate regularly and feel \"normal,\" but the average probability that any given month leads to a baby gets lower with time."
            ]
          },
          {
            "title": "Natural chances of pregnancy",
            "subtitle": "How age affects conception",
            "content": "Age doesn't flip fertility on or off. It changes probabilities:",
            "bulletPoints": [
              "In the late 20s to early 30s, most healthy couples have a good chance of conceiving within a year of trying (if there are no underlying issues).",
              "In the mid–late 30s, fertility declines faster. It can take longer to conceive, and miscarriage rates increase.",
              "By the early 40s, natural conception is still possible for some, but it becomes less common; by around 45, spontaneous pregnancy is rare, even when cycles are still regular."
            ]
          },
          {
            "title": "Miscarriage and age",
            "subtitle": "Understanding the risks",
            "content": "Age also affects the chance that a pregnancy will continue. Because older eggs are more likely to have chromosomal problems, the risk of miscarriage rises with age:",
            "bulletPoints": [
              "In younger women, miscarriage risk is often estimated around 10-15% per pregnancy.",
              "By the late 30s and early 40s, the risk increases noticeably.",
              "By 45, the risk of miscarriage is significantly higher: some studies approach 40-50%.",
              "These numbers are averages, not destiny. If you're older and have had miscarriages, age itself can be a factor, not something you \"did wrong.\""
            ]
          },
          {
            "title": "Ovarian reserve tests",
            "subtitle": "What they really mean",
            "content": "Because eggs can't be counted directly, doctors use ovarian reserve tests to get a sense of \"egg supply\":",
            "bulletPoints": [
              "AMH (Anti-Müllerian Hormone), Antral follicle count (AFC) via ultrasound, and sometimes FSH + estradiol on cycle day 2–3.",
              "Younger age + higher AMH/AFC → usually better response to treatment.",
              "Older age + lower AMH/AFC → often fewer eggs retrieved, may need more cycles.",
              "Important: Ovarian reserve tests do not tell you for sure whether you can get pregnant naturally. They are not a fertility \"score.\"",
              "They are most useful for planning: timing of treatments, how aggressive to be, and what to expect from egg freezing or IVF."
            ]
          },
          {
            "title": "Age and ART",
            "subtitle": "IVF, egg freezing, donor eggs",
            "content": "Age also shapes what we can reasonably expect from fertility treatments.",
            "bulletPoints": [
              "IVF success rates per cycle are usually highest under 35, then decline with each age bracket (35–37, 38–40, 40–42, 43+).",
              "For many clinics, live birth rates per transfer drop noticeably after 38–40, especially with own eggs.",
              "Egg freezing: Eggs frozen in the early-mid 30s tend to have higher quality than eggs frozen in the early 40s.",
              "The number of eggs needed for a reasonable chance of a future baby increases with age.",
              "Donor eggs largely bypass the age-related decline in egg quality, often leading to success rates similar to the (typically younger) donor age group."
            ]
          },
          {
            "title": "Age is a factor, not a verdict",
            "subtitle": "Taking action",
            "content": "All of this can sound intimidating, but the goal is not to scare you. It's to help you plan earlier if possible, set realistic expectations, and choose the right strategy.",
            "bulletPoints": [
              "If you're under 35 and trying to conceive, many guidelines suggest seeking help after 12 months of trying (or earlier if you have symptoms, irregular cycles, or known conditions).",
              "If you're 35 or older, it's reasonable to seek an evaluation after 6 months of trying.",
              "If you're considering egg freezing, earlier is generally better, but \"the best time\" is also when it fits your life, finances, and emotional readiness.",
              "Age is something you can't change, but how you respond to it is in your hands: getting informed, checking your situation, and building a plan with a doctor you trust."
            ]
          }
        ],
        "references": [
          {
            "title": "Fertility and age. J Hum Reprod Sci. 2010 Sep;3(3):121-3.",
            "authors": "George K, Kamath MS.",
            "url": "https://scholar.google.com/scholar?q=Fertility%20and%20age.%20J%20Hum%20Reprod%20Sci.%202010%20Sep%3B3(3)%3A121-3.%20George%20K%2C%20Kamath%20MS.",
            "evidenceLevel": "review"
          },
          {
            "title": "Human Ovarian Reserve from Conception to the Menopause. PLOS ONE 5(1): e8772",
            "authors": "Wallace WHB, Kelsey TW.",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Human%20Ovarian%20Reserve%20from%20Conception%20to%20the%20Menopause.%20PLOS%20ONE%205(1)%3A%20e8772%20Wallace%20WHB%2C%20Kelsey%20TW.%202010",
            "evidenceLevel": "review"
          },
          {
            "title": "Changes with age in the level and duration of fertility in the menstrual cycle. Human reproduction vol",
            "authors": "Dunson, David B et al.",
            "journal": "17,5",
            "year": 2002,
            "url": "https://scholar.google.com/scholar?q=Changes%20with%20age%20in%20the%20level%20and%20duration%20of%20fertility%20in%20the%20menstrual%20cycle.%20Human%20reproduction%20vol%20Dunson%2C%20David%20B%20et%20al.%202002",
            "evidenceLevel": "review"
          },
          {
            "title": "Maternal age-specific rates of numerical chromosome abnormalities with special reference to trisomy. Human genetics vol",
            "authors": "Hassold, T, and D Chiu.",
            "journal": "70,1",
            "year": 1985,
            "url": "https://scholar.google.com/scholar?q=Maternal%20age-specific%20rates%20of%20numerical%20chromosome%20abnormalities%20with%20special%20reference%20to%20trisomy.%20Human%20genetics%20vol%20Hassold%2C%20T%2C%20and%20D%20Chiu.%201985",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-pcos-and-getting-pregnant",
      "kind": "article",
      "tags": ["moon", "moon-guides", "her", "pcos"],
      "title": "PCOS and getting pregnant",
      "subtitle": "Symptoms, diagnosis, and fertility",
      "readTimeMinutes": 8,
      "body": {
        "introduction": "Polycystic ovary syndrome (PCOS) is a common hormonal condition that affects how the ovaries work. It's estimated to affect around 8-13% of people with ovaries of reproductive age, though many are never formally diagnosed. With PCOS, the ovaries don't always release an egg each month (ovulation), and the body often makes higher-than-usual levels of androgens (hormones like testosterone). This hormonal imbalance can affect periods, skin and hair, metabolism, and fertility.",
        "quickSummary": [
          {
            "title": "It's common.",
            "description": "PCOS affects 8-13% of people with ovaries of reproductive age. Not everyone looks the same: some have almost no symptoms, others have many."
          },
          {
            "title": "Ovulation is the key issue.",
            "description": "Irregular or absent ovulation makes cycles unpredictable and conception harder, but many people with PCOS do conceive with support."
          },
          {
            "title": "It's more than fertility.",
            "description": "PCOS affects periods, metabolic health, and mental health. Good care looks at the whole picture."
          }
        ],
        "sections": [
          {
            "title": "Common signs and symptoms",
            "subtitle": "What PCOS can look like",
            "content": "Not everyone with PCOS looks the same. You don't need all of these features to have PCOS. Many guidelines say a diagnosis can be made if two out of three are present: irregular ovulation, signs of high androgens, polycystic ovaries on ultrasound.",
            "bulletPoints": [
              "Irregular or absent periods: Cycles that are very long, very short, or unpredictable. Some people bleed rarely; others bleed for a long time.",
              "Signs of higher androgens: Acne (often persistent or more severe than typical), increased hair growth on the face, chest, stomach, or thighs (hirsutism), thinning hair on the scalp.",
              "Ovulation problems: Because ovulation is irregular or absent, it can be harder to predict fertile days or get pregnant without help.",
              "Polycystic-appearing ovaries: On ultrasound, the ovaries may show many small resting follicles (\"cysts\" are really tiny fluid-filled follicles, not dangerous tumors).",
              "Metabolic features (in some people): Weight gain or difficulty losing weight, insulin resistance or higher blood sugar, higher risk of prediabetes or type 2 diabetes over time."
            ]
          },
          {
            "title": "How is PCOS diagnosed?",
            "subtitle": "The diagnostic process",
            "content": "There's no single \"PCOS test.\" A diagnosis is usually based on multiple factors. Importantly, PCOS is a spectrum: some people are \"lean PCOS,\" others have more metabolic issues.",
            "bulletPoints": [
              "Detailed history and symptoms: Cycle length, weight changes, acne, hair growth, family history, etc.",
              "Physical examination: Looking at skin, hair distribution, blood pressure, body mass index (BMI), waist circumference.",
              "Blood tests: To check hormone levels (androgens, LH/FSH, prolactin, TSH, sometimes AMH) and to exclude other conditions that can look like PCOS.",
              "Pelvic ultrasound: To look at the number and appearance of follicles in each ovary and ovarian size.",
              "Your care plan should be adapted to your profile and goals (pregnancy now, later, or symptom control only)."
            ]
          },
          {
            "title": "PCOS and fertility",
            "subtitle": "What it means for conception",
            "content": "PCOS is one of the most common causes of ovulatory infertility, meaning it can make it harder to release an egg regularly. The reassuring part is that many people with PCOS do conceive.",
            "bulletPoints": [
              "Irregular ovulation can make cycles unpredictable and increase the time it takes to get pregnant.",
              "Lifestyle changes (when relevant): movement, nutrition, sleep, stress care.",
              "Ovulation-inducing medications (like letrozole or clomiphene) are commonly recommended first-line.",
              "Metformin in selected cases, especially if insulin resistance is present.",
              "ART such as IUI or IVF if needed.",
              "Your doctor should help you choose a step-by-step plan depending on your age, partner's sperm, how long you've been trying, and any other conditions."
            ]
          },
          {
            "title": "Living with PCOS",
            "subtitle": "Beyond fertility",
            "content": "Even if you're not trying to conceive, PCOS deserves proper care. Good PCOS care looks at the whole picture: periods, fertility plans, metabolic health, and mental health.",
            "bulletPoints": [
              "Menstrual health: Very infrequent periods can increase the risk of endometrial (uterine lining) problems; your provider may recommend ways to protect the lining (e.g., progestin, hormonal contraception).",
              "Metabolic health: PCOS is linked with a higher risk of insulin resistance, prediabetes, type 2 diabetes, and sometimes high cholesterol and blood pressure over time.",
              "Mental health: PCOS is associated with higher rates of anxiety, depression, and body-image distress. You're not \"overreacting\" if this feels emotionally heavy."
            ]
          },
          {
            "title": "When to seek help",
            "subtitle": "Signs to discuss with a clinician",
            "content": "You can start with a gynecologist, reproductive endocrinologist, or sometimes an informed primary care provider, depending on your health system.",
            "bulletPoints": [
              "Your cycles are longer than 35 days, very irregular, or you skip months.",
              "You've been trying to conceive for 12 months if under 35, or 6 months if 35 or older.",
              "You notice bothersome hair growth changes, scalp hair loss, or acne that doesn't respond to usual care.",
              "You have a known PCOS diagnosis and new concerns about weight, blood sugar, or mood."
            ]
          }
        ],
        "references": [
          {
            "title": "International evidence-based guideline for the assessment and management of PCOS.",
            "authors": "International PCOS Network",
            "url": "https://www.monash.edu/medicine/mchri/pcos/guideline",
            "evidenceLevel": "guideline"
          },
          {
            "title": "FAQ: Polycystic Ovary Syndrome (PCOS). American College of Obstetricians and Gynecologists.",
            "authors": "ACOG",
            "url": "https://www.acog.org/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Recommendations from the international evidence-based guideline for PCOS",
            "authors": "Teede HJ, et al.",
            "journal": "Human Reproduction",
            "year": 2018,
            "url": "https://scholar.google.com/scholar?q=Recommendations%20from%20the%20international%20evidence-based%20guideline%20for%20PCOS%20Teede%20HJ%2C%20et%20al.%202018",
            "evidenceLevel": "review"
          },
          {
            "title": "Evaluation and Treatment of Polycystic Ovary Syndrome",
            "authors": "Legro RS.",
            "journal": "N Engl J Med",
            "year": 2016,
            "url": "https://scholar.google.com/scholar?q=Evaluation%20and%20Treatment%20of%20Polycystic%20Ovary%20Syndrome%20Legro%20RS.%202016",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-endometriosis-and-getting-pregnant",
      "kind": "article",
      "tags": ["moon", "moon-guides", "her", "endo"],
      "title": "Endometriosis and getting pregnant",
      "subtitle": "Symptoms, diagnosis, and fertility",
      "readTimeMinutes": 8,
      "body": {
        "introduction": "Endometriosis is a long-term condition that affects many people with a uterus, but it's still often misunderstood. It can cause significant pain, fatigue, and sometimes difficulties getting pregnant.",
        "quickSummary": [
          {
            "title": "Tissue grows outside the uterus.",
            "description": "In endometriosis, tissue similar to the uterine lining grows on the ovaries, tubes, or other pelvic organs, causing inflammation and pain."
          },
          {
            "title": "It's common.",
            "description": "Around 1 in 10 women of reproductive age is estimated to have endometriosis. Some have obvious symptoms, others discover it when exploring fertility."
          },
          {
            "title": "Fertility is possible.",
            "description": "Not everyone with endometriosis will have trouble conceiving. Many do become pregnant naturally, though 30-50% may experience difficulties."
          }
        ],
        "sections": [
          {
            "title": "What is endometriosis?",
            "subtitle": "The basics",
            "content": "In a typical cycle, the lining of the uterus (endometrium) builds up, then sheds during the period. In endometriosis, tissue similar to this lining grows outside the uterus, on the ovaries, fallopian tubes, the outer surface of the uterus, or other organs in the pelvis.",
            "bulletPoints": [
              "This tissue still responds to hormones, which means it can thicken and bleed each month, but the blood has nowhere to go.",
              "Over time, this can lead to inflammation, pain, cysts (endometriomas), and scar tissue that causes organs to stick together.",
              "We still don't know exactly why endometriosis develops. The most accepted theory is \"retrograde menstruation\": during the period, some menstrual blood flows backwards through the fallopian tubes into the pelvis.",
              "Hormones (especially estrogen), genetics, and the immune system also seem to play a role."
            ]
          },
          {
            "title": "What does it feel like?",
            "subtitle": "Common symptoms",
            "content": "Endometriosis can affect day-to-day life in many ways. One important point: the level of pain does not always match the severity of disease.",
            "bulletPoints": [
              "Very painful periods that interfere with work, school, or daily activities.",
              "Pelvic pain between periods.",
              "Pain during or after sex.",
              "Pain with bowel movements or urination, especially around the period.",
              "Heavy or irregular bleeding.",
              "Bloating and fatigue.",
              "Someone with \"mild\" endometriosis on scan can have disabling pain, and someone with advanced disease can feel almost normal. That's part of why the condition is often under-recognised."
            ]
          },
          {
            "title": "How can endometriosis affect fertility?",
            "subtitle": "Understanding the link",
            "content": "Not everyone with endometriosis will have trouble conceiving. Many people do become pregnant naturally. However, compared with the general population, endometriosis is linked to a higher risk of infertility.",
            "bulletPoints": [
              "The pelvis can become more inflamed, and that inflammatory environment may interfere with egg quality, fertilisation, and early embryo development.",
              "Scar tissue and adhesions can change the shape of the pelvis. Fallopian tubes may be blocked or kinked, or the ovary may no longer sit close to the tube.",
              "Endometriomas (\"chocolate cysts\") on the ovaries can sometimes be associated with reduced ovarian reserve, and surgery on them can also remove or damage healthy ovarian tissue.",
              "Painful intercourse and chronic pain can reduce sexual frequency or make sex more stressful.",
              "Even with these challenges, fertility is not \"all or nothing\". Age, ovarian reserve, severity and location of disease, and other fertility factors all influence your personal chances."
            ]
          },
          {
            "title": "How is endometriosis diagnosed?",
            "subtitle": "The diagnostic process",
            "content": "There is no single blood test that confirms endometriosis. Diagnosis usually starts with a careful medical history (your pain pattern, periods, bowel or bladder symptoms, and family history), followed by a pelvic exam.",
            "bulletPoints": [
              "Transvaginal ultrasound can detect endometriomas and, in expert hands, some forms of deep endometriosis.",
              "MRI can be useful in more complex cases, especially when bowel, bladder, or deeper ligaments are involved.",
              "The \"gold standard\" for diagnosis is laparoscopy: a keyhole surgery where a small camera is inserted into the abdomen to directly see lesions and, if needed, treat them.",
              "Major guidelines recommend reserving surgery for situations where it will change management: severe pain, large cysts, unclear diagnosis, or specific fertility questions."
            ]
          },
          {
            "title": "Treatment for pain and daily life",
            "subtitle": "Managing symptoms",
            "content": "There is currently no cure that makes endometriosis disappear forever, but there are effective ways to manage pain. Your treatment plan depends on your goals: pain relief, improving functioning, preserving fertility, or getting pregnant soon.",
            "bulletPoints": [
              "Pain medication, such as NSAIDs (ibuprofen or similar).",
              "Hormonal treatments (the pill, progestins, hormonal IUD, or GnRH analogues) to reduce bleeding and flare-ups when you're not trying to conceive.",
              "Surgery (laparoscopy) to remove lesions and adhesions: can help pain and sometimes fertility, but needs to be balanced against risk of scar tissue and damage to ovarian reserve.",
              "Lifestyle and supportive care: physical activity adapted to your pain level, pelvic floor physiotherapy, pain education, and mental health support."
            ]
          },
          {
            "title": "Treatment for fertility",
            "subtitle": "Your options",
            "content": "Your team will usually suggest a complete fertility workup first (ovarian reserve tests, ultrasound, partner's semen analysis if relevant, and sometimes tubal tests). Then they can help you decide between options.",
            "bulletPoints": [
              "Expectant management (trying naturally for a period of time, if tubes are open and overall picture is reassuring).",
              "IUI (intrauterine insemination) in some mild cases.",
              "IVF/ICSI, especially when disease is moderate to severe, tubes are affected, ovarian reserve is reduced, or you've been trying for a long time.",
              "Egg freezing if you're not ready for pregnancy but want to preserve options for later.",
              "There is no single \"correct\" path. For some people, surgery before ART can improve outcomes; for others, going directly to IVF is more efficient than repeated surgery.",
              "Decisions are best made together with a team experienced in both endometriosis and fertility, taking your age, reserve, symptoms, and preferences into account."
            ]
          }
        ],
        "references": [
          {
            "title": "ESHRE guideline: endometriosis (latest update). European Society of Human Reproduction and Embryology.",
            "authors": "ESHRE Guideline Group",
            "url": "https://www.eshre.eu/Guidelines-and-Legal",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Endometriosis and infertility: a committee opinion",
            "authors": "ASRM Practice Committee",
            "journal": "Fertil Steril",
            "year": 2012,
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Endometriosis: A guide for patients (Endometriosis booklet).",
            "authors": "ASRM",
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "Endometriosis and infertility",
            "authors": "Bulletti C, et al.",
            "journal": "J Assist Reprod Genet",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Endometriosis%20and%20infertility%20Bulletti%20C%2C%20et%20al.%202010",
            "evidenceLevel": "review"
          },
          {
            "title": "Endometriosis. N Engl J Med",
            "authors": "Giudice LC.",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Endometriosis.%20N%20Engl%20J%20Med%20Giudice%20LC.%202010",
            "evidenceLevel": "review"
          },
          {
            "title": "Endometriosis and infertility: pathophysiology and management",
            "authors": "De Ziegler D, et al.",
            "journal": "Lancet",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Endometriosis%20and%20infertility%3A%20pathophysiology%20and%20management%20De%20Ziegler%20D%2C%20et%20al.%202010",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-art-with-pcos",
      "kind": "article",
      "tags": ["moon", "moon-guides", "her", "pcos", "during-cycle"],
      "title": "Going through ART with PCOS",
      "subtitle": "Navigating fertility treatment with PCOS",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Polycystic ovary syndrome (PCOS) is one of the most common reasons people are referred for fertility treatment. The good news: many people with PCOS do get pregnant, either with simple ovulation medications or with assisted reproductive technologies (ART) like IUI and IVF. This guide walks through what it means to do ART with PCOS, what's different, and what you can do to feel more prepared and in control.",
        "quickSummary": [
          {
            "title": "PCOS responds well to treatment.",
            "description": "Many people with PCOS conceive with ovulation medications or ART. IVF success rates can be similar to or better than other diagnoses."
          },
          {
            "title": "\"Low and slow\" approach.",
            "description": "Clinics use tailored protocols with lower doses to get good egg numbers while minimizing the risk of ovarian hyperstimulation (OHSS)."
          },
          {
            "title": "Lifestyle matters.",
            "description": "Even modest weight loss (5-10%) can improve ovulation and ART outcomes. Metabolic optimization makes a real difference."
          }
        ],
        "sections": [
          {
            "title": "Why PCOS affects fertility",
            "subtitle": "Understanding the basics",
            "content": "PCOS doesn't mean you're \"infertile,\" but it does change how your ovaries work. ART aims to recreate what isn't happening reliably by itself: help you ovulate, fertilise an egg, and support implantation.",
            "bulletPoints": [
              "Irregular or absent ovulation: Many cycles are anovulatory (no egg released), so there are fewer chances to conceive.",
              "More small follicles, fewer \"dominant\" ones: On ultrasound, ovaries often show many small follicles, but they don't always grow and ovulate on their own.",
              "Hormonal imbalance: Androgens and insulin resistance can interfere with regular cycles and egg maturation."
            ]
          },
          {
            "title": "First steps before ART",
            "subtitle": "Setting the stage",
            "content": "Before moving straight to IVF, many people with PCOS will go through a few key steps.",
            "bulletPoints": [
              "Ovulation induction: First-line meds are usually oral tablets like letrozole (and sometimes clomiphene), which help your ovary grow and release one egg.",
              "Guidelines now generally favour letrozole over clomiphene for PCOS because it gives better ovulation and pregnancy rates with lower multiple-pregnancy risk.",
              "If sperm quality is OK, ovulation induction can be combined with timed intercourse or IUI (intrauterine insemination).",
              "You might go through a few cycles of this before moving on to IVF, depending on your age, test results, and how you respond to medications."
            ]
          },
          {
            "title": "Metabolic & lifestyle optimisation",
            "subtitle": "Small changes, real difference",
            "content": "Even small changes can make a real difference. Your team may suggest addressing these alongside or before ART, not instead of it.",
            "bulletPoints": [
              "Weight and insulin resistance: For people with higher BMI or insulin resistance, modest weight loss (even 5-10%) can improve ovulation and ART outcomes in PCOS.",
              "Metformin: Sometimes used to improve insulin sensitivity and cycle regularity, especially if there's prediabetes or diabetes.",
              "The Mediterranean diet is commonly recommended for having a positive impact on PCOS patients.",
              "Smoking, alcohol, sleep, stress, and diet all matter for egg quality, response to treatment, and long-term health."
            ]
          },
          {
            "title": "IVF with PCOS",
            "subtitle": "What's different?",
            "content": "IVF can be very effective in PCOS, but the approach is slightly different from someone with low ovarian reserve. People with PCOS often have a high antral follicle count (AFC) and can respond very strongly to stimulation.",
            "bulletPoints": [
              "More eggs ≠ always better eggs. It's often possible to get good egg numbers, but if stimulation is too aggressive, the ovaries can over-respond.",
              "Clinics usually take a \"low-and-slow\" approach to keep you safe while still getting good-quality eggs.",
              "Your doctor might start with lower doses of gonadotropins than in someone with low reserve.",
              "Use a GnRH antagonist protocol and possibly a GnRH agonist trigger instead of hCG to reduce OHSS risk.",
              "Plan a \"freeze-all\" strategy (freeze embryos and transfer later) if your response is very strong."
            ]
          },
          {
            "title": "OHSS: the main risk",
            "subtitle": "What to watch for",
            "content": "People with PCOS are more prone to OHSS (ovarian hyperstimulation syndrome), a condition where swollen ovaries and fluid shifts can cause pain, bloating, and in rare cases more serious complications.",
            "bulletPoints": [
              "Most clinics now use modern protocols that have dramatically lowered the risk.",
              "Warning signs: Rapid weight gain (e.g., >2 kg in a couple of days), severe bloating, abdominal pain, or shortness of breath.",
              "Feeling very unwell, dizzy, or unable to keep food/fluids down.",
              "If this happens, contact your clinic urgently."
            ]
          },
          {
            "title": "Embryos, transfer, and success rates",
            "subtitle": "The outlook",
            "content": "The overall outlook with IVF for PCOS is often quite good, especially in younger patients.",
            "bulletPoints": [
              "Egg numbers can be high, which gives more embryos to work with.",
              "When weight, insulin resistance, and hormones are reasonably well controlled, pregnancy and live birth rates with IVF in PCOS can be similar to or better than other infertility diagnoses of the same age group.",
              "Some challenges remain: Egg and embryo quality may be variable if metabolic health is not optimised.",
              "Some people need several cycles to get to a healthy pregnancy.",
              "Miscarriage risk can be slightly higher, particularly if there are other factors like obesity or uncontrolled diabetes."
            ]
          },
          {
            "title": "Other ART options in PCOS",
            "subtitle": "Depending on your situation",
            "content": "Your doctor may also discuss other options depending on your circumstances.",
            "bulletPoints": [
              "IUI with controlled ovarian stimulation: Often an intermediate step between basic ovulation induction and IVF.",
              "Fertility preservation: Freezing eggs or embryos earlier in life if you're not ready to try for pregnancy now but worry about future fertility."
            ]
          },
          {
            "title": "Preparing yourself for ART",
            "subtitle": "What can help",
            "content": "You don't have to fix everything before starting treatment, but some things can help.",
            "bulletPoints": [
              "Clarify your goals: Is your priority speed, minimising risk, or maximising the chance per cycle? This can influence protocol choices.",
              "Ask specific questions: How will you reduce my risk of OHSS? What stimulation protocol do you typically use for PCOS? What's the plan if I respond too strongly or too weakly?",
              "Support your body: Aim for regular movement, balanced meals, and enough sleep. Work on blood sugar and weight if recommended.",
              "Support your mind: PCOS and ART can be emotionally heavy. Consider counselling, support groups, or online communities specifically for PCOS + infertility."
            ]
          }
        ],
        "references": [
          {
            "title": "Recommendations from the 2023 international evidence-based guideline for the assessment and management of PCOS",
            "authors": "Teede HJ, et al.",
            "journal": "ASRM.",
            "url": "https://scholar.google.com/scholar?q=Recommendations%20from%20the%202023%20international%20evidence-based%20guideline%20for%20the%20assessment%20and%20management%20of%20PCOS%20Teede%20HJ%2C%20et%20al.",
            "evidenceLevel": "review"
          },
          {
            "title": "Diagnosis and treatment of polycystic ovary syndrome: an Endocrine Society clinical practice guideline",
            "authors": "Legro RS, et al.",
            "journal": "J Clin Endocrinol Metab",
            "year": 2013,
            "url": "https://scholar.google.com/scholar?q=Diagnosis%20and%20treatment%20of%20polycystic%20ovary%20syndrome%3A%20an%20Endocrine%20Society%20clinical%20practice%20guideline%20Legro%20RS%2C%20et%20al.%202013",
            "evidenceLevel": "review"
          },
          {
            "title": "The prevalence and phenotypic features of PCOS: a systematic review and meta-analysis",
            "authors": "Bozdag G, et al.",
            "journal": "Hum Reprod",
            "year": 2016,
            "url": "https://scholar.google.com/scholar?q=The%20prevalence%20and%20phenotypic%20features%20of%20PCOS%3A%20a%20systematic%20review%20and%20meta-analysis%20Bozdag%20G%2C%20et%20al.%202016",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-when-tubes-are-blocked",
      "kind": "article",
      "tags": ["moon", "moon-guides", "her", "practical"],
      "title": "When the tubes are blocked",
      "subtitle": "Understanding tubal factor infertility",
      "readTimeMinutes": 5,
      "body": {
        "introduction": "The fallopian tubes play a crucial role in conception. Each month, around ovulation, an ovary releases an egg which is picked up by the tube. Fertilisation usually happens inside the tube, not in the uterus. Tiny hair-like cells (cilia) then help move the fertilised egg down into the uterus, where it can implant. If the tube is blocked, scarred, or its inner lining is damaged, this journey is disrupted.",
        "quickSummary": [
          {
            "title": "Tubes are where fertilisation happens.",
            "description": "The egg and sperm meet in the tube, not the uterus. Blocked or damaged tubes can prevent this meeting or increase ectopic pregnancy risk."
          },
          {
            "title": "One tube may be enough.",
            "description": "If one tube is blocked but one is open, natural conception is still possible. It may just take longer."
          },
          {
            "title": "IVF bypasses the tubes.",
            "description": "When both tubes are blocked or badly damaged, IVF is usually needed because it bypasses the tubes entirely."
          }
        ],
        "sections": [
          {
            "title": "What do \"blocked\" or \"damaged\" tubes mean?",
            "subtitle": "The spectrum of tubal problems",
            "content": "Tubal problems sit on a spectrum. Sometimes the tubes look fine on imaging, but previous infections or inflammation have damaged the inner cilia, so function is impaired even without a \"hard\" blockage on tests.",
            "bulletPoints": [
              "One tube blocked, one open: You can still conceive naturally, but it can take longer.",
              "Both tubes blocked or badly damaged: Natural conception is very unlikely; most people will need IVF, because IVF bypasses the tubes.",
              "Hydrosalpinx: A tube blocked and filled with fluid. That fluid can leak back into the uterus and make implantation less likely, even with IVF."
            ]
          },
          {
            "title": "Common causes of tubal damage",
            "subtitle": "How it happens",
            "content": "You can't always pinpoint an exact cause, but frequent culprits include infections, surgery, and endometriosis. Sometimes, tubal damage is discovered in people with no clear past infection or surgery.",
            "bulletPoints": [
              "Pelvic infections (PID): Especially untreated or repeated infections with chlamydia or gonorrhea. These infections can silently damage the tubes, even if symptoms were mild or absent.",
              "Past ectopic pregnancy: An ectopic pregnancy in the tube can leave scarring, even if it was treated.",
              "Endometriosis: Deposits and adhesions can pull or twist tubes, or cause inflammation around them.",
              "Previous pelvic or abdominal surgery: Appendectomy, surgery for ovarian cysts, caesarean section, bowel surgery. Surgery can sometimes lead to adhesions that affect the tubes.",
              "Tuberculosis or other rare infections (more common in some regions of the world)."
            ]
          },
          {
            "title": "How do doctors check if the tubes are okay?",
            "subtitle": "Testing options",
            "content": "There are a few main tests. Your doctor may recommend one or more depending on your situation, age, history, symptoms, and whether surgery might be useful for other reasons.",
            "bulletPoints": [
              "Hysterosalpingogram (HSG): An X-ray test where dye is gently injected through the cervix to see the shape of the uterus and whether the fallopian tubes are open or blocked.",
              "Sonohysterography / HyCoSy: An ultrasound test where saline (and sometimes contrast) is put into the uterus through a small catheter to check the cavity and watch fluid flow through the tubes in real time.",
              "Laparoscopy with chromotubation: A keyhole surgery under anesthesia where the doctor looks inside the pelvis with a camera and injects blue dye through the uterus to directly see if the tubes are open."
            ]
          },
          {
            "title": "Living with tubal factor infertility",
            "subtitle": "What you can control",
            "content": "Many people discover tubal problems years after an infection or surgery they barely remember, or with no clear cause at all. It's easy to blame yourself, but tubal factor is often silent and outside your control.",
            "bulletPoints": [
              "Get clear information and a thorough workup.",
              "Ask for written explanations of test results (HSG reports, laparoscopy notes).",
              "Talk through options: try naturally, surgery, IVF, or a mix.",
              "Consider a second opinion if you're unsure about major choices like surgery."
            ]
          }
        ],
        "references": [
          {
            "title": "Fertility evaluation of infertile women: a committee opinion",
            "authors": "ASRM",
            "journal": "Fertil Steril",
            "year": 2021,
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "The role of tubal surgery in the era of assisted reproductive technologies: a committee opinion",
            "authors": "ASRM",
            "journal": "Fertil Steril",
            "year": 2015,
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "Fertility problems: assessment and management (CG156)",
            "authors": "NICE",
            "year": 2013,
            "url": "https://www.nice.org.uk/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Ectopic pregnancy and miscarriage: diagnosis and initial management (NG126).",
            "authors": "NICE",
            "url": "https://www.nice.org.uk/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Ectopic pregnancy and miscarriage – Information for you.",
            "authors": "RCOG",
            "url": "https://www.rcog.org.uk/",
            "evidenceLevel": "guideline"
          }
        ]
      }
    },
    {
      "id": "new-article-polyps-fibroids-uterus",
      "kind": "article",
      "tags": ["moon", "moon-guides", "her", "practical"],
      "title": "Polyps, fibroids, and your uterus",
      "subtitle": "When the uterus gets in the way of pregnancy",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "The uterus (womb) is the place where a pregnancy implants and grows. For many people, all the focus is put on eggs and sperm, but the \"nest\" also matters. Some conditions in the uterus can make it harder to get pregnant or to stay pregnant, even when ovulation and sperm are normal.",
        "quickSummary": [
          {
            "title": "Location matters most.",
            "description": "Not all fibroids or polyps affect fertility. What matters is where they are and whether they distort the uterine cavity."
          },
          {
            "title": "Many are treatable.",
            "description": "Polyps, cavity-distorting fibroids, adhesions, and septums can often be corrected with hysteroscopic surgery."
          },
          {
            "title": "Many people still conceive.",
            "description": "Having a uterine condition doesn't automatically mean you can't get pregnant. Many have successful pregnancies with or without treatment."
          }
        ],
        "sections": [
          {
            "title": "What do we mean by \"uterine causes\"?",
            "subtitle": "Understanding uterine factor",
            "content": "When doctors talk about \"uterine factor,\" they usually mean problems inside the uterus that can make it harder for an embryo to implant, increase miscarriage risk, or sometimes cause heavy or painful periods.",
            "bulletPoints": [
              "\"Acquired\" changes: fibroids, polyps, or scar tissue.",
              "\"Congenital\" (from birth) differences: like a septate or bicornuate uterus.",
              "Not everyone with a uterine condition has fertility problems. The question is: does this particular issue, in this particular size and location, matter for your fertility?"
            ]
          },
          {
            "title": "Fibroids (myomas)",
            "subtitle": "Location matters more than the word itself",
            "content": "Fibroids are benign (non-cancerous) muscle growths in the wall of the uterus. They're extremely common.",
            "bulletPoints": [
              "Submucosal: grow inside the cavity of the uterus. These can lower implantation rates or increase miscarriage risk.",
              "Intramural: mostly in the wall of the uterus.",
              "Subserosal: grow toward the outside. They often don't affect fertility and can be left alone.",
              "From a fertility perspective, fibroids that distort the uterine cavity matter most."
            ]
          },
          {
            "title": "Endometrial polyps",
            "subtitle": "Small overgrowths of the lining",
            "content": "Polyps are small, soft overgrowths of the uterine lining (endometrium). They can cause irregular spotting or sometimes no symptoms at all.",
            "bulletPoints": [
              "Because they sit directly in the cavity where an embryo tries to implant, polyps are often removed when someone has infertility or repeated IVF failure.",
              "Removing a polyp is usually a small hysteroscopic procedure."
            ]
          },
          {
            "title": "Uterine adhesions (Asherman's syndrome)",
            "subtitle": "Scar tissue inside the uterus",
            "content": "Adhesions are scar tissue inside the uterus, often after curettage, some infections, or previous uterine surgeries.",
            "bulletPoints": [
              "They can partially or completely \"stick\" the walls of the uterus together.",
              "This may cause lighter periods, no periods at all, and difficulty getting or staying pregnant.",
              "Adhesions are usually treated with hysteroscopic surgery to gently remove scar tissue and re-open the cavity."
            ]
          },
          {
            "title": "Congenital uterine differences",
            "subtitle": "Shape from birth",
            "content": "Some people are born with a uterus that's a bit different in shape. Not all uterine shapes cause problems.",
            "bulletPoints": [
              "Septate uterus (a partition in the middle): most clearly linked to higher miscarriage risk because the septum may have poorer blood flow. Can often be corrected with hysteroscopic surgery.",
              "Bicornuate uterus (more \"heart-shaped\" with two horns): may increase risks of miscarriage or preterm birth, but many people still carry pregnancies successfully.",
              "Other Müllerian anomalies vary in their impact on fertility and pregnancy."
            ]
          },
          {
            "title": "How are uterine problems diagnosed?",
            "subtitle": "Testing options",
            "content": "Depending on symptoms and context, your doctor may suggest several types of imaging or procedures.",
            "bulletPoints": [
              "Transvaginal ultrasound: first-line imaging for fibroids, adenomyosis, and some structural issues.",
              "Saline sonohysterography: ultrasound with fluid to better see the inside of the cavity.",
              "Hysteroscopy: a small camera inserted through the cervix to directly look inside the uterus (and often treat at the same time).",
              "MRI: sometimes used for complex cases, adenomyosis, or detailed mapping of fibroids or uterine shape."
            ]
          },
          {
            "title": "What does this mean if I want a baby?",
            "subtitle": "The bigger picture",
            "content": "Having a \"uterine factor\" can sound scary, but it doesn't automatically mean you can't get pregnant. Your doctor will look at your age, ovarian reserve, how long you've been trying, miscarriage history, and what exactly is seen in the uterus.",
            "bulletPoints": [
              "Small polyps or submucosal fibroids can be removed and the cavity restored.",
              "Adhesions can be treated, with close follow-up.",
              "A septate uterus can sometimes be reshaped to reduce miscarriage risk.",
              "Even with fibroids or a different shape, many people have successful pregnancies with or without ART.",
              "Your doctor can explain if the finding is likely a main cause, a contributing factor, or just a \"side finding\" that doesn't need treatment right now."
            ]
          }
        ],
        "references": [
          {
            "title": "Uterine factors affecting reproduction: a committee opinion",
            "authors": "ASRM Practice Committee",
            "journal": "Fertil Steril",
            "year": 2021,
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Prevalence and diagnosis of congenital uterine anomalies in women with reproductive failure",
            "authors": "Saravelos SH, et al.",
            "journal": "Hum Reprod Update",
            "year": 2008,
            "url": "https://scholar.google.com/scholar?q=Prevalence%20and%20diagnosis%20of%20congenital%20uterine%20anomalies%20in%20women%20with%20reproductive%20failure%20Saravelos%20SH%2C%20et%20al.%202008",
            "evidenceLevel": "review"
          },
          {
            "title": "Fibroids and infertility: an updated systematic review of the evidence",
            "authors": "Pritts EA, et al.",
            "journal": "Fertil Steril",
            "year": 2009,
            "url": "https://scholar.google.com/scholar?q=Fibroids%20and%20infertility%3A%20an%20updated%20systematic%20review%20of%20the%20evidence%20Pritts%20EA%2C%20et%20al.%202009",
            "evidenceLevel": "review"
          },
          {
            "title": "Hysteroscopy for treating subfertility associated with suspected major uterine cavity abnormalities",
            "authors": "Bosteels J, et al.",
            "journal": "Cochrane Database Syst Rev",
            "year": 2015,
            "url": "https://scholar.google.com/scholar?q=Hysteroscopy%20for%20treating%20subfertility%20associated%20with%20suspected%20major%20uterine%20cavity%20abnormalities%20Bosteels%20J%2C%20et%20al.%202015",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-art-with-endometriosis",
      "kind": "article",
      "tags": ["moon", "moon-guides", "her", "endo", "during-cycle"],
      "title": "Going through ART with endometriosis",
      "subtitle": "What it really means for your journey",
      "readTimeMinutes": 9,
      "body": {
        "introduction": "Living with endometriosis can feel like carrying two burdens at once: chronic pain and uncertainty about fertility. Many people are told about \"scar tissue,\" \"cysts,\" or \"inflammation\" just as they start thinking about pregnancy or fertility preservation. The key message: many people with endometriosis do get pregnant, others build their families with help from treatments like IUI or IVF, and there are evidence-based strategies to protect your fertility and manage symptoms.",
        "quickSummary": [
          {
            "title": "Many do conceive.",
            "description": "Not everyone with endometriosis has fertility problems. Roughly 30-50% experience some difficulty, but that means 50-70% conceive without major intervention."
          },
          {
            "title": "Surgery isn't always first.",
            "description": "Repeat surgeries can reduce ovarian reserve. For many, well-timed IVF may offer better chances than \"just one more surgery.\""
          },
          {
            "title": "Global management matters.",
            "description": "Modern care isn't \"surgery OR IVF\". It's about using surgery, medication, lifestyle, and ART in a way that fits you."
          }
        ],
        "sections": [
          {
            "title": "How endometriosis can affect fertility",
            "subtitle": "In simple terms",
            "content": "In endometriosis, tissue similar to the uterine lining grows outside the uterus. Each cycle, this tissue can bleed and trigger inflammation in places where it shouldn't.",
            "bulletPoints": [
              "Create scar tissue and adhesions, which can bend or block the tubes, or \"glue\" organs together.",
              "Lead to endometriomas (cysts) on the ovaries, which may slowly affect ovarian reserve.",
              "Change the pelvic environment, possibly affecting egg quality and implantation conditions.",
              "This doesn't mean you \"won't\" get pregnant. It means you and your team may need a more structured plan, especially as age and time trying increase."
            ]
          },
          {
            "title": "When fertility isn't the main goal (yet)",
            "subtitle": "Focusing on pain and protection",
            "content": "If pregnancy isn't a short-term goal, treatment usually focuses on controlling pain, reducing inflammation, and protecting quality of life.",
            "bulletPoints": [
              "Many effective treatments for pain (combined pill, progestins, hormonal IUD, GnRH analogues) are contraceptive: you can't use them while actively trying to conceive.",
              "If you're diagnosed young with severe disease or large endometriomas, some teams may discuss egg freezing before repeated surgeries or long-term hormonal suppression.",
              "This isn't mandatory for everyone, but worth asking if you're under 35 with stage III/IV endometriosis, or facing repeat ovarian surgery."
            ]
          },
          {
            "title": "When you're ready to try for pregnancy",
            "subtitle": "The whole picture",
            "content": "Once having a baby becomes a real goal, your team will look at: your age, how long you've been trying, ovarian reserve (AMH, antral follicle count), whether tubes are open, sperm quality, the stage and location of your endometriosis, and any previous surgeries or ART attempts.",
            "bulletPoints": [
              "Time-limited natural trying (with or without ovulation meds).",
              "Intrauterine insemination (IUI) in selected cases.",
              "In vitro fertilisation (IVF/ICSI), sometimes with surgery before or between cycles.",
              "Most international guidelines support moving quicker to IVF when you're over 35, have been trying for a while, have moderate–severe endometriosis, or other factors are present."
            ]
          },
          {
            "title": "Surgery vs. going straight to IVF",
            "subtitle": "A tricky question",
            "content": "There isn't a one-size-fits-all answer. Surgery may be useful when pain is severe, there are large endometriomas, organs are affected, or diagnosis is unclear.",
            "bulletPoints": [
              "Every ovarian surgery (especially on endometriomas) carries a risk of removing healthy ovarian tissue and reducing ovarian reserve.",
              "Repeat surgeries are particularly concerning. Guidelines now emphasise avoiding repeated pelvic surgery purely \"to improve fertility\" when a person already qualifies for IVF.",
              "Reserve surgery for clear indications: uncontrolled pain, suspicious lesions, severe distortion, or hydrosalpinx that must be treated.",
              "For many people with endometriosis-related infertility, especially in their late 30s, well-timed IVF may offer better overall chances than \"just one more surgery.\""
            ]
          },
          {
            "title": "IVF with endometriosis",
            "subtitle": "What changes?",
            "content": "IVF doesn't cure endometriosis, but it bypasses many of the mechanical issues.",
            "bulletPoints": [
              "Eggs are collected directly from the ovaries → blocked or twisted tubes matter less.",
              "Fertilisation happens in the lab → sperm doesn't need to swim through an inflamed pelvis.",
              "Embryos are placed directly into the uterus at the best time in your cycle.",
              "Your team may choose a stimulation protocol that balances egg yield and comfort, or consider a freeze-all strategy to optimise the uterine environment.",
              "These are nuanced decisions; you're allowed to ask why they recommend one path over another."
            ]
          },
          {
            "title": "Does ART make endometriosis worse?",
            "subtitle": "Addressing the concern",
            "content": "Many people worry that \"all those hormones\" will make their endometriosis flare permanently.",
            "bulletPoints": [
              "Short term: ovarian stimulation can increase bloating, pelvic discomfort, and pain. This is usually temporary and monitored closely.",
              "Long term: there is no strong evidence that a few IVF cycles dramatically worsen endometriosis progression.",
              "Tell your team clearly what you live with day-to-day. Ask about specific pain plans for stimulation and after egg retrieval.",
              "You deserve symptom-aware care, not just \"standard IVF.\""
            ]
          },
          {
            "title": "Global management",
            "subtitle": "An integrated approach",
            "content": "Modern guidelines increasingly talk about global or integrated management, not \"surgery OR IVF,\" but how to use all tools in a way that fits you.",
            "bulletPoints": [
              "Controlling pain and inflammation enough that you can function.",
              "Protecting ovarian reserve (avoiding unnecessary repeat surgery).",
              "Using ART at the right time for your age and time trying.",
              "Considering fertility preservation earlier in some high-risk cases.",
              "The important thing is that there is a strategy, and that you feel involved in it, not just carried along."
            ]
          },
          {
            "title": "Emotional side",
            "subtitle": "You're not \"late\" or failing",
            "content": "Endometriosis often comes with a heavy emotional load. People frequently describe feeling \"behind\" their friends, guilty for not trying earlier, or afraid they're \"running out of time.\"",
            "bulletPoints": [
              "These feelings are common and valid, but they're not a sign you've failed. Your body didn't choose endometriosis.",
              "Helpful supports: a psychologist or counsellor who knows endometriosis and fertility, support groups, bringing someone you trust to big appointments.",
              "Good care is not just about more hormones or more surgery. It's also about being seen as a whole person."
            ]
          },
          {
            "title": "Questions to ask your team",
            "subtitle": "You can literally read these in your consult",
            "content": "Clear answers won't solve everything, but they can make the road feel less frightening.",
            "bulletPoints": [
              "Based on my age, ovarian reserve, and stage of endometriosis, what are my realistic options (natural, IUI, IVF, donor)?",
              "Do you think surgery would mainly help my pain, my fertility, or both? What are the risks for my ovarian reserve?",
              "If we go to IVF, how do my chances roughly compare to someone my age without endometriosis?",
              "Should we consider egg freezing now (if I'm not ready for pregnancy yet) or embryo freezing if I'm doing IVF?",
              "How will we manage my pain and symptoms during and between treatments?",
              "If this plan doesn't work, what would plan B look like?"
            ]
          }
        ],
        "references": [
          {
            "title": "ESHRE guideline: endometriosis (latest update). European Society of Human Reproduction and Embryology.",
            "authors": "ESHRE Guideline Group",
            "url": "https://www.eshre.eu/Guidelines-and-Legal",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Endometriosis and infertility: a committee opinion",
            "authors": "ASRM Practice Committee",
            "journal": "Fertil Steril",
            "year": 2012,
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Endometriosis: A guide for patients (Endometriosis booklet).",
            "authors": "ASRM",
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "Endometriosis and infertility",
            "authors": "Bulletti C, et al.",
            "journal": "J Assist Reprod Genet",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Endometriosis%20and%20infertility%20Bulletti%20C%2C%20et%20al.%202010",
            "evidenceLevel": "review"
          },
          {
            "title": "Endometriosis. N Engl J Med",
            "authors": "Giudice LC.",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Endometriosis.%20N%20Engl%20J%20Med%20Giudice%20LC.%202010",
            "evidenceLevel": "review"
          },
          {
            "title": "Endometriosis and infertility: pathophysiology and management",
            "authors": "De Ziegler D, et al.",
            "journal": "Lancet",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Endometriosis%20and%20infertility%3A%20pathophysiology%20and%20management%20De%20Ziegler%20D%2C%20et%20al.%202010",
            "evidenceLevel": "review"
          },
          {
            "title": "Assisted reproduction in endometriosis",
            "authors": "De Ziegler D, et al.",
            "journal": "Semin Reprod Med",
            "year": 2019,
            "url": "https://scholar.google.com/scholar?q=Assisted%20reproduction%20in%20endometriosis%20De%20Ziegler%20D%2C%20et%20al.%202019",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-varicoceles-and-male-fertility",
      "kind": "article",
      "tags": ["moon", "moon-guides", "his", "sperm"],
      "title": "Varicoceles and male fertility",
      "subtitle": "Understanding varicoceles and their impact on fertility",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "Varicocele is a common condition affecting around 15-20% of adult men, and up to 40% of men assessed for infertility. It is characterized by abnormal dilatation of the spermatic cord veins in the testes, often compared to varicose veins, but located in the testicles. The first symptoms of varicocele may appear in adolescence or later. Symptoms and their intensity vary from person to person, and for some men, varicocele can be very disabling.",
        "quickSummary": [
          {
            "title": "Common condition.",
            "description": "Varicocele affects 15-20% of men and up to 40% of men assessed for infertility."
          },
          {
            "title": "Impacts fertility.",
            "description": "Can impair sperm quality and reduce ability to conceive, and may cause testicular atrophy."
          },
          {
            "title": "Treatable.",
            "description": "Management depends on symptoms and fertility impact, ranging from monitoring to surgery."
          }
        ],
        "sections": [
          {
            "title": "What causes varicocele?",
            "subtitle": "Understanding the condition",
            "content": "The exact cause of varicocele is unclear. However, it seems that anatomy plays a role. The veins of the spermatic cord help drain blood from the testicles to the body. If the valves that regulate blood flow in these veins don't work properly, blood can pool and cause the veins to dilate. This condition is more common in the left testicle, probably due to the position of the left testicular vein."
          },
          {
            "title": "Impact on fertility",
            "subtitle": "Why it matters",
            "content": "Varicocele is a common cause of male infertility. It can impair sperm quality and reduce a man's ability to conceive. In addition, varicocele can cause atrophy (reduction in size) of the testes, which can also affect fertility. Around 40% of men assessed for infertility problems have a varicocele."
          },
          {
            "title": "Symptoms",
            "subtitle": "What to look for",
            "content": "While some varicoceles are asymptomatic, others may cause various symptoms:",
            "bulletPoints": [
              "Testicular pain that may worsen throughout the day.",
              "Scrotal swelling or a feeling of heaviness.",
              "Testicular atrophy (reduction in size).",
              "A soft, \"worm sack-like\" mass above the testicle.",
              "Symptoms may worsen with prolonged standing or physical exertion."
            ]
          },
          {
            "title": "Diagnosis",
            "subtitle": "How it's detected",
            "content": "Varicocele can be diagnosed during a physical examination by a urologist. If symptoms are unclear, a scrotal ultrasound may be performed. This examination uses sound waves to produce images of the testicles and can help confirm the presence of varicocele and determine its size."
          },
          {
            "title": "Treatment and recommendations",
            "subtitle": "Management options",
            "content": "Treatment of varicocele depends on the severity of symptoms and the impact on fertility:",
            "bulletPoints": [
              "For men experiencing significant pain: over-the-counter analgesics, avoidance of activities that aggravate pain, and wearing scrotal support.",
              "If varicocele affects fertility or causes testicular atrophy: surgery may be recommended to ligate or obstruct the dilated veins.",
              "Varicocelectomy is usually performed under local or general anesthetic and has a high success rate.",
              "Regular monitoring may be recommended for men who have a varicocele but no significant symptoms or fertility problems."
            ]
          }
        ],
        "references": [
          {
            "title": "Report on varicocele and infertility: a committee opinion",
            "authors": "Practice Committee of ASRM",
            "journal": "Fertility and Sterility",
            "year": 2014,
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "When is a varicocele repair indicated: the dilemma of hypogonadism and erectile dysfunction? Asian Journal of Andrology",
            "authors": "Dabaja, A.A., Goldstein, M.",
            "year": 2016,
            "url": "https://scholar.google.com/scholar?q=When%20is%20a%20varicocele%20repair%20indicated%3A%20the%20dilemma%20of%20hypogonadism%20and%20erectile%20dysfunction%3F%20Asian%20Journal%20of%20Andrology%20Dabaja%2C%20A.A.%2C%20Goldstein%2C%20M.%202016",
            "evidenceLevel": "review"
          },
          {
            "title": "The great debate: varicocele treatment and impact on fertility",
            "authors": "Will MA, et al.",
            "journal": "Fertil Steril",
            "year": 2011,
            "url": "https://scholar.google.com/scholar?q=The%20great%20debate%3A%20varicocele%20treatment%20and%20impact%20on%20fertility%20Will%20MA%2C%20et%20al.%202011",
            "evidenceLevel": "review"
          },
          {
            "title": "Varicocele and male infertility",
            "authors": "Jensen CFS, et al.",
            "journal": "Nat Rev Urol",
            "year": 2017,
            "url": "https://scholar.google.com/scholar?q=Varicocele%20and%20male%20infertility%20Jensen%20CFS%2C%20et%20al.%202017",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-art-with-uterine-issue",
      "kind": "article",
      "tags": ["moon", "moon-guides", "her", "during-cycle", "practical"],
      "title": "Going through ART with a uterine issue",
      "subtitle": "How uterine factors influence your ART plan",
      "readTimeMinutes": 5,
      "body": {
        "introduction": "Once your uterus has been assessed and, if needed, treated, your ART journey often follows the usual structure (stimulation, egg retrieval, fertilisation, embryo transfer)… but with a few extra considerations.",
        "quickSummary": [
          {
            "title": "Timing matters.",
            "description": "Your team may freeze embryos first, treat the uterus, then transfer once the cavity looks good, especially if you're older or need complex surgery."
          },
          {
            "title": "Lining preparation may be tested.",
            "description": "If you've had surgery or lining problems, a \"mock\" cycle can help find the best hormone regimen before transfer."
          },
          {
            "title": "Single embryo transfer is often safer.",
            "description": "With unusual cavity shapes, transferring one embryo reduces twin risk and complications."
          }
        ],
        "sections": [
          {
            "title": "Timing of IVF and embryo transfer",
            "subtitle": "Strategic planning",
            "content": "Sometimes your team will do IVF and freeze all embryos first, then treat the uterus (e.g., remove fibroids or adhesions), and transfer embryos only once the cavity looks good.",
            "bulletPoints": [
              "This can be helpful if you are older and don't want to delay egg retrieval.",
              "Or if you need more complex surgery and need time to heal before pregnancy.",
              "In other cases, they'll treat the uterus first, then start IVF afterwards."
            ]
          },
          {
            "title": "Lining preparation and \"mock\" cycles",
            "subtitle": "Optimising for transfer",
            "content": "If you've had uterine surgery or lining problems (thin endometrium, adhesions), your team may run additional preparation cycles.",
            "bulletPoints": [
              "A \"mock\" endometrial preparation cycle to see how thick and regular your lining gets with hormones.",
              "Adjust estrogen and progesterone doses, or use different regimens to find what works best.",
              "This helps them choose the safest and most promising moment for embryo transfer."
            ]
          },
          {
            "title": "Number of embryos to transfer",
            "subtitle": "Why single may be better",
            "content": "If you have a smaller or unusual cavity shape (e.g., uterine malformation), your doctor may recommend a single embryo transfer.",
            "bulletPoints": [
              "This reduces the risk of twins, which would put extra pressure on a uterus that's already at higher risk of preterm birth or complications.",
              "This can feel frustrating when you want to \"maximize\" chances, but it's often safer for both you and future babies."
            ]
          },
          {
            "title": "Living with a uterine factor during ART",
            "subtitle": "Symptoms, risks, and reassurance",
            "content": "A uterine factor can raise specific concerns: heavy or painful periods, spotting between cycles, fear of miscarriage or preterm labour, or fear that \"my uterus won't work.\"",
            "bulletPoints": [
              "It's important to hear clearly from your team: What is your baseline risk (not just general statistics)?",
              "What can be done to optimise the lining and cavity before transfer?",
              "What extra monitoring will you get in early pregnancy and beyond?",
              "Many people with corrected polyps, septa, or cavity-distorting fibroids go on to have uncomplicated IVF pregnancies.",
              "Some with complex anomalies may need more monitoring or specialist obstetric care, but pregnancy is still absolutely possible."
            ]
          }
        ],
        "references": [
          {
            "title": "Uterine factors affecting reproduction: a committee opinion",
            "authors": "ASRM Practice Committee",
            "journal": "Fertil Steril",
            "year": 2021,
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Prevalence and diagnosis of congenital uterine anomalies in women with reproductive failure",
            "authors": "Saravelos SH, et al.",
            "journal": "Hum Reprod Update",
            "year": 2008,
            "url": "https://scholar.google.com/scholar?q=Prevalence%20and%20diagnosis%20of%20congenital%20uterine%20anomalies%20in%20women%20with%20reproductive%20failure%20Saravelos%20SH%2C%20et%20al.%202008",
            "evidenceLevel": "review"
          },
          {
            "title": "Fibroids and infertility: an updated systematic review",
            "authors": "Pritts EA, et al.",
            "journal": "Fertil Steril",
            "year": 2009,
            "url": "https://scholar.google.com/scholar?q=Fibroids%20and%20infertility%3A%20an%20updated%20systematic%20review%20Pritts%20EA%2C%20et%20al.%202009",
            "evidenceLevel": "review"
          },
          {
            "title": "Hysteroscopy for treating subfertility associated with uterine cavity abnormalities",
            "authors": "Bosteels J, et al.",
            "journal": "Cochrane Database Syst Rev",
            "year": 2015,
            "url": "https://scholar.google.com/scholar?q=Hysteroscopy%20for%20treating%20subfertility%20associated%20with%20uterine%20cavity%20abnormalities%20Bosteels%20J%2C%20et%20al.%202015",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-sex-erections-ttc",
      "kind": "article",
      "tags": ["moon", "moon-guides", "his", "sperm", "emotional"],
      "title": "Sex, erections, and trying to conceive",
      "subtitle": "How erections, fertility, and overall health connect",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "If you're trying to conceive and erections feel less reliable than before, it can be scary, and it's easy to assume it means \"something is wrong with my fertility.\" The truth is more nuanced: erectile dysfunction and fertility are related, but not the same thing.",
        "quickSummary": [
          {
            "title": "ED ≠ infertility.",
            "description": "Erectile dysfunction affects timing and frequency of sex, but doesn't automatically mean sperm quality is affected."
          },
          {
            "title": "Pressure is common.",
            "description": "Timed intercourse around ovulation creates performance anxiety, a major cause of situational ED during the fertility journey."
          },
          {
            "title": "It's treatable.",
            "description": "ED is very treatable with lifestyle changes, counseling, and sometimes medication. Don't push through silently."
          }
        ],
        "sections": [
          {
            "title": "What erectile dysfunction actually means",
            "subtitle": "Defining ED",
            "content": "Erectile dysfunction is the consistent inability to get or keep an erection firm enough for satisfying sex. It's common to have occasional difficulty, and it's often linked to things like stress, fatigue, or drinking too much alcohol.",
            "bulletPoints": [
              "ED becomes more common with age, but it can happen at any age.",
              "It's also very common in the infertility setting, where pressure and timing can make sex feel like a performance."
            ]
          },
          {
            "title": "Does ED mean you're infertile?",
            "subtitle": "Not necessarily",
            "content": "Fertility is mainly about whether sperm can reach an egg (and whether sperm quality and reproductive anatomy/hormones support that). Erections are about whether sex (or sperm collection) can happen comfortably and effectively.",
            "bulletPoints": [
              "ED doesn't automatically mean sperm are \"bad.\"",
              "But ED can still reduce your chances of pregnancy because it can make it harder to have sex often enough, at the right times, or to ejaculate inside the vagina.",
              "Fertility experts highlight sexual dysfunction (including ED) as relevant because it can interfere with conception efforts and deserves real evaluation and support."
            ]
          },
          {
            "title": "Why ED shows up during the fertility journey",
            "subtitle": "The pressure loop",
            "content": "A lot of ED during \"trying to conceive\" is about pressure, especially timed intercourse (sex scheduled around ovulation). Research in infertile couples shows higher stress during the fertile window and that sexual dysfunction is common in male partners.",
            "bulletPoints": [
              "Stress → weaker erections",
              "Weaker erections → more stress and avoidance",
              "Less sex → more fertility anxiety",
              "The good news is that this pattern is common and treatable, and it's absolutely something you can bring up at a fertility clinic."
            ]
          },
          {
            "title": "ED can be a health signal",
            "subtitle": "Not just a bedroom issue",
            "content": "Sometimes ED is mostly situational. But frequent ED can also be linked to underlying health factors.",
            "bulletPoints": [
              "Common causes and contributors: high blood pressure, high cholesterol, diabetes, depression/anxiety, hormone problems, and medication side effects.",
              "Some of the same conditions that affect erections (blood vessel health, hormones, metabolic health) can also be associated with sperm health and overall reproductive well-being.",
              "Treating ED can sometimes be part of improving overall health, not just improving sex."
            ]
          },
          {
            "title": "What usually helps",
            "subtitle": "And what to ask for",
            "content": "If ED is affecting conception, you deserve a plan, just like you would for any other fertility factor. Start with a simple medical check-in.",
            "bulletPoints": [
              "A fertility clinic or GP/urologist may review: medications (some can contribute to ED), cardiovascular risk factors and metabolic health, mental health, stress, relationship strain, and hormones when relevant (especially if libido is low too).",
              "ED is very treatable. Many people improve with a mix of lifestyle and stress support, counseling (especially when \"timed sex\" adds pressure).",
              "When appropriate, medication might be prescribed, but because they aren't right for everyone and can interact with other health conditions, the safest step is to discuss them with a clinician rather than self-treat.",
              "In the fertility setting, counseling/sex therapy can be genuinely powerful, especially when the main driver is performance anxiety, timed-intercourse pressure, or relationship strain."
            ]
          },
          {
            "title": "When to seek help",
            "subtitle": "Sooner rather than later",
            "content": "Consider booking support if:",
            "bulletPoints": [
              "ED is happening \"often\" (not just once in a while).",
              "You're avoiding sex because of fear it won't work.",
              "Erections are fine alone but unreliable during timed intercourse (classic pressure pattern).",
              "You also have low libido, pain, major mood symptoms, or you're on medications that might contribute.",
              "You have cardiovascular risk factors (high blood pressure, diabetes, high cholesterol, smoking, etc.), because ED can be a health marker."
            ]
          }
        ],
        "references": [
          {
            "title": "Diagnostic evaluation of sexual dysfunction in the male partner in the setting of infertility",
            "authors": "ASRM",
            "journal": "Committee Opinion",
            "year": 2018,
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "Erectile Dysfunction Guideline. American Urological Association",
            "authors": "AUA",
            "year": 2018,
            "url": "https://www.auanet.org/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Erectile dysfunction (causes and overview).",
            "authors": "NHS",
            "url": "https://www.nhs.uk/",
            "evidenceLevel": "official"
          },
          {
            "title": "Stress and sexual dysfunction in male partners of infertile couples",
            "authors": "Song et al.",
            "year": 2016,
            "url": "https://scholar.google.com/scholar?q=Stress%20and%20sexual%20dysfunction%20in%20male%20partners%20of%20infertile%20couples%20Song%20et%20al.%202016",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-emotional-support",
      "kind": "article",
      "tags": ["emotional", "her", "practical"],
      "title": "Emotional support during fertility care",
      "subtitle": "You're not alone",
      "readTimeMinutes": 5,
      "body": {
        "introduction": "Infertility isn't \"just\" a medical problem. It can touch every part of your life: your mood, your relationships, your body image, your sense of identity, even your plans for the future. If you've felt overwhelmed, numb, angry, or like you're \"not yourself\" anymore, you are absolutely not alone. Research consistently shows that people facing infertility are more likely to experience symptoms of anxiety and depression than the general population. This isn't you being \"too sensitive\". It's a very human response to a very hard situation.",
        "quickSummary": [
          {
            "title": "It's a valid struggle.",
            "description": "The emotional burden of infertility can be as heavy as living with other serious medical illnesses. Your feelings are real and shared by many."
          },
          {
            "title": "Stress doesn't cause infertility.",
            "description": "But taking care of your mental health is part of taking care of your overall health in a difficult medical situation."
          },
          {
            "title": "Needing help isn't weakness.",
            "description": "It means you've been strong for too long without enough support. Help is available, and you deserve it."
          }
        ],
        "sections": [
          {
            "title": "Why infertility hits so hard",
            "subtitle": "Understanding the impact",
            "content": "A few things make infertility uniquely tough. None of this means you're weak. It means you're responding in a very normal way to a prolonged, high-stress, high-stakes situation.",
            "bulletPoints": [
              "It strikes at something deeply personal: For many people, the ability to have children is tied to identity, family, culture, or a life story imagined since childhood.",
              "It's full of uncertainty and waiting: You often don't know if it will work or when. Every cycle brings a new round of hope, monitoring, then disappointment or another wait.",
              "It can be invisible and isolating: From the outside, you may look \"fine.\" Friends or family may say unhelpful things like \"just relax,\" making you feel misunderstood.",
              "Treatment itself is intense: Tests, procedures, hormones, side effects, financial stress, scheduling around monitoring visits: it's a full-time mental job."
            ]
          },
          {
            "title": "Common feelings",
            "subtitle": "That many others share too",
            "content": "Everyone is different, but some themes come up again and again among people going through infertility.",
            "bulletPoints": [
              "Grief and loss: Not necessarily loss of a baby, but loss of the \"simple\" path you hoped for. Loss of time, spontaneity, and the version of life where getting pregnant was easy.",
              "Anxiety and constant mental noise: Thoughts like \"What if this never works?\" or \"Did I miss my window?\" Studies show elevated rates of anxiety in people going through ART.",
              "Shame and self-blame: Thinking \"My body is failing\" or \"This is my fault\", even though you would never judge someone else that way.",
              "Anger and envy: Feeling pain when someone announces a pregnancy is extremely common. You can be happy for others and still feel devastated for yourself.",
              "Strain in relationships: Partners may cope differently. Sex can start to feel like a medical procedure. Friendships may drift if people don't understand."
            ]
          },
          {
            "title": "It's not \"just in your head\"",
            "subtitle": "The evidence is nuanced",
            "content": "People sometimes worry that if they admit how low they feel, others will tell them to \"think positive\" or imply that stress caused their infertility.",
            "bulletPoints": [
              "Stress and mood do not cause most structural or hormonal fertility problems on their own.",
              "At the same time, ongoing high stress and depression can make everything harder: keeping up with treatment, sleeping and eating well, staying connected.",
              "Taking care of your mental health is not a luxury or a side issue. It's part of taking care of your overall health."
            ]
          },
          {
            "title": "Ways to cope and feel less alone",
            "subtitle": "Small, doable supports",
            "content": "You don't have to fix everything at once. Think in terms of small, achievable supports.",
            "bulletPoints": [
              "Put words on what you're feeling: Write in a journal, use voice memos, or share with someone you trust. Naming feelings often takes away some of their power.",
              "Protect your mental \"bandwidth\": Mute social media accounts, say no to events that feel too painful, ask friends not to surprise you with pregnancy announcements.",
              "Find people who get it: Support groups and online communities give you a place where you don't have to explain the basics.",
              "Consider professional support: A therapist who understands infertility can help untangle guilt, navigate decisions, and build coping strategies."
            ]
          },
          {
            "title": "When to reach out urgently",
            "subtitle": "Important warning signs",
            "content": "It's really important to seek help quickly (emergency services, crisis line, or your doctor) if you notice these signs. This does not mean you are weak. It means your nervous system is overwhelmed and needs more support.",
            "bulletPoints": [
              "Persistent thoughts like \"everyone would be better off without me.\"",
              "Self-harm ideas or behaviours.",
              "You can't get out of bed, go to work, or do basic daily tasks.",
              "Panic attacks that don't ease, or crushing anxiety every day.",
              "Drinking, drugs, or other behaviours spiralling to cope."
            ]
          }
        ],
        "references": [
          {
            "title": "Anxiety and depression among infertile women: a cross-sectional study from Hungary",
            "authors": "Lakatos E, et al.",
            "journal": "BMC Womens Health",
            "year": 2017,
            "url": "https://scholar.google.com/scholar?q=Anxiety%20and%20depression%20among%20infertile%20women%3A%20a%20cross-sectional%20study%20from%20Hungary%20Lakatos%20E%2C%20et%20al.%202017",
            "evidenceLevel": "review"
          },
          {
            "title": "The National Infertility Association – Helpful Advice & Support Resources.",
            "authors": "RESOLVE",
            "url": "https://resolve.org/",
            "evidenceLevel": "reference"
          },
          {
            "title": "Prevalence of psychiatric disorders in infertile women and men undergoing IVF treatment",
            "authors": "Volgsten H, et al.",
            "journal": "Hum Reprod",
            "year": 2008,
            "url": "https://scholar.google.com/scholar?q=Prevalence%20of%20psychiatric%20disorders%20in%20infertile%20women%20and%20men%20undergoing%20IVF%20treatment%20Volgsten%20H%2C%20et%20al.%202008",
            "evidenceLevel": "review"
          },
          {
            "title": "Psychological distress in women with fertility problems: a longitudinal study",
            "authors": "Frederiksen Y, et al.",
            "journal": "Acta Obstet Gynecol Scand",
            "year": 2015,
            "url": "https://scholar.google.com/scholar?q=Psychological%20distress%20in%20women%20with%20fertility%20problems%3A%20a%20longitudinal%20study%20Frederiksen%20Y%2C%20et%20al.%202015",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-supporting-partner-ivf",
      "kind": "article",
      "tags": ["emotional", "both-partners", "practical"],
      "title": "Supporting your partner through IVF",
      "subtitle": "How to be present during ART",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "Assisted reproductive treatment (ART) can be physically demanding, emotionally intense, and full of waiting. If you're the partner of someone going through IVF, IUI, ICSI, egg retrieval, embryo transfer, or fertility preservation, you might feel unsure of what to say, how to help, or where you fit in. The most supportive partners aren't the ones who always \"say the perfect thing.\" They're the ones who stay present, make the load lighter, and keep the relationship feeling like a team.",
        "quickSummary": [
          {
            "title": "Ask before assuming.",
            "description": "\"Do you want me to listen, help you decide, or distract you?\" prevents misunderstandings and shows you're paying attention."
          },
          {
            "title": "Practical help is emotional help.",
            "description": "Taking over logistics, handling meals, driving to appointments: these reduce stress and land as love."
          },
          {
            "title": "Don't forget yourself.",
            "description": "Supporting someone doesn't mean you're not allowed to struggle. Find your own support and take care of yourself too."
          }
        ],
        "sections": [
          {
            "title": "Understand what ART can feel like",
            "subtitle": "From the inside",
            "content": "Even when things are going \"normally,\" ART can bring significant challenges. You don't need to fully understand it to be supportive. You just need to take it seriously.",
            "bulletPoints": [
              "A heavy mental load: appointments, meds, timing, forms.",
              "A loss of control: waiting on results, changing plans.",
              "Body stress: hormonal shifts, bloating, fatigue, pain.",
              "Identity pressure: \"my body is failing,\" \"I'm letting us down.\"",
              "Loneliness: especially if they feel they're carrying most of it."
            ]
          },
          {
            "title": "Practical support is emotional support",
            "subtitle": "Help that reduces stress",
            "content": "In ART, help that reduces stress often lands as love. A good rule: don't ask \"What can I do?\" every time. Take the initiative.",
            "bulletPoints": [
              "Take over logistics: calendar, reminders, pharmacy pickups, insurance/admin calls.",
              "Handle food and comfort basics: groceries, easy meals, warm water bottle, clean sheets.",
              "Drive or escort them to key appointments (retrieval day especially).",
              "Protect recovery time: run interference with social plans, chores, and \"shoulds.\"",
              "Make \"result days\" lighter: fewer commitments, a gentle plan for after the call."
            ]
          },
          {
            "title": "Be careful with \"silver-lining\" language",
            "subtitle": "What to say instead",
            "content": "When someone is anxious or grieving, reassurance can accidentally feel dismissive. Hope is good, just don't force it.",
            "bulletPoints": [
              "Instead of \"Don't worry, it'll work\" → try \"This is a lot. I'm here.\"",
              "Instead of \"At least we can try again\" → try \"It makes sense you feel this way.\"",
              "Instead of \"Be positive\" → try \"We'll get through today together.\"",
              "Ask: \"Do you want comfort, problem-solving, or quiet?\""
            ]
          },
          {
            "title": "Make space for mixed feelings",
            "subtitle": "Permission to be human",
            "content": "ART can create emotional whiplash: hope, fear, excitement, guilt, jealousy, anger, numbness, all in the same week.",
            "bulletPoints": [
              "Let your partner have their feelings without trying to fix them immediately.",
              "Often what they need most is permission to be human."
            ]
          },
          {
            "title": "Protect the relationship",
            "subtitle": "From becoming \"a project\"",
            "content": "When everything revolves around treatment, couples can start feeling like coworkers on a medical plan. Timed intercourse, performance anxiety, changes in libido, and body discomfort are common.",
            "bulletPoints": [
              "Agree on a \"no pressure\" signal. Normalize breaks.",
              "Focus on connection over outcome.",
              "Touch without expectations: hugs, massages, cuddling, without it leading to sex.",
              "Avoid comments that sound like performance reviews (\"we have to do it tonight\").",
              "If sex becomes stressful or painful, it's okay to ask for help. Many clinics can refer to counseling or sexual health support."
            ]
          },
          {
            "title": "Show up on the hard days",
            "subtitle": "And plan for them",
            "content": "ART has predictable emotional spikes. It's good to be aware and prepared.",
            "bulletPoints": [
              "Before big procedures.",
              "Waiting for fertilization/embryo updates.",
              "The two-week wait.",
              "Negative tests or disappointing results.",
              "Miscarriage or cycle cancellation."
            ]
          },
          {
            "title": "Don't forget your own feelings",
            "subtitle": "You matter too",
            "content": "Supporting someone doesn't mean you're not allowed to struggle. You might feel helpless, scared, guilty, or pressured to be \"the strong one.\"",
            "bulletPoints": [
              "Tell your partner what you feel in a non-blaming way.",
              "Find your own support (friend, therapist, support group).",
              "Agree on how you'll handle questions from family/friends.",
              "When you take care of yourself, you show up better for them."
            ]
          }
        ],
        "references": [
          {
            "title": "ReproductiveFacts: Emotional support and counseling guidance.",
            "authors": "ASRM",
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "National Infertility Association: Relationship-focused advice for couples navigating infertility.",
            "authors": "RESOLVE",
            "url": "https://resolve.org/",
            "evidenceLevel": "reference"
          }
        ]
      }
    },
    {
      "id": "new-article-waiting-for-result",
      "kind": "article",
      "tags": ["post-cycle", "emotional", "practical"],
      "title": "The two-week wait",
      "subtitle": "What's normal while waiting for the test",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "The days between your treatment and your pregnancy test can feel endless. Whether you did an IUI, IVF, or a frozen embryo transfer, this \"waiting window\" is one of the most emotionally intense parts of the process, and it's normal to overanalyze every cramp, every twinge, and every symptom.",
        "quickSummary": [
          {
            "title": "Symptoms aren't reliable.",
            "description": "Most symptoms come from progesterone or normal hormonal shifts and appear in both pregnant and non-pregnant cycles."
          },
          {
            "title": "Don't stop meds early.",
            "description": "Even if you're spotting or feel like your period is coming, keep taking medications until your clinic says otherwise."
          },
          {
            "title": "Wait for your test date.",
            "description": "Early testing can cause false results. Your clinic's scheduled date reduces confusing outcomes."
          }
        ],
        "sections": [
          {
            "title": "Why the wait exists",
            "subtitle": "The science behind the timing",
            "content": "Pregnancy tests measure hCG, a hormone that rises after implantation. Early on, hCG can be too low to detect, even if everything is progressing normally, so testing early can create an unnecessary \"negative\" that later turns positive.",
            "bulletPoints": [
              "If you had an hCG \"trigger shot\" (like Ovidrel), it can temporarily make a test look positive because the test may detect the medication.",
              "Early testing can lead to false reassurance or false panic.",
              "That's why your clinic gives you a specific test date: it's chosen to reduce confusing results."
            ]
          },
          {
            "title": "When is the pregnancy test usually done?",
            "subtitle": "Typical timelines",
            "content": "Timelines vary by clinic and by cycle type, but common patterns are:",
            "bulletPoints": [
              "After embryo transfer (IVF or FET): many clinics do a blood pregnancy test (\"beta hCG\") about 9–14 days after transfer, depending on embryo stage and protocol.",
              "After IUI: many clinics test around 2 weeks after the insemination.",
              "If your clinic gave you a date, that's the date to follow, even if you feel symptoms, feel nothing, or start bleeding."
            ]
          },
          {
            "title": "Symptoms during the wait",
            "subtitle": "What shows up in both outcomes",
            "content": "This is the frustrating truth: most symptoms are not specific. They often come from progesterone, estrogen shifts, the procedures themselves, or your body doing normal luteal-phase things.",
            "bulletPoints": [
              "Cramping / pelvic heaviness: Mild cramps or \"period-like\" sensations are common and can happen in both pregnant and non-pregnant cycles.",
              "Bloating, fatigue, breast tenderness, mood swings: Very common, especially if you're taking progesterone support. These side effects can mimic early pregnancy symptoms almost perfectly.",
              "Spotting or discharge: Light spotting (pink/brown) or watery discharge can happen after transfer, after IUI, or from progesterone use. It can be normal.",
              "No symptoms: Also normal. Many people feel nothing and still have a positive beta."
            ]
          },
          {
            "title": "Bleeding during the wait",
            "subtitle": "What it can (and can't) mean",
            "content": "Bleeding is one of the hardest things to interpret. It can happen for several reasons: cervical irritation, medication effects, timing of your period, or sometimes implantation-related spotting.",
            "bulletPoints": [
              "Key point: bleeding alone doesn't tell you the outcome.",
              "Unless your clinic instructs otherwise, keep taking your medications and test on the scheduled date."
            ]
          },
          {
            "title": "A crucial rule: don't stop your meds early",
            "subtitle": "Why this matters",
            "content": "Even if you're convinced your period is coming, or you're spotting, or you feel \"different\": don't stop progesterone/estrogen support unless your clinic tells you to. Many clinics emphasize continuing meds on time during the wait."
          },
          {
            "title": "When to contact your clinic",
            "subtitle": "Warning signs to watch for",
            "content": "Call your clinic promptly if you have:",
            "bulletPoints": [
              "Heavy bleeding (soaking pads quickly)",
              "Pain that's getting worse instead of better",
              "Fever, chills, or foul-smelling discharge",
              "Severe nausea/vomiting or feeling unusually unwell"
            ]
          },
          {
            "title": "When to seek urgent care / ER",
            "subtitle": "Emergency signs",
            "content": "Seek urgent care now if you have:",
            "bulletPoints": [
              "Sudden, severe abdominal or pelvic pain",
              "Dizziness, fainting, weakness",
              "Shoulder-tip pain",
              "Heavy bleeding with pain or feeling faint",
              "These can be warning signs that need immediate evaluation, including (rarely) complications like ectopic pregnancy."
            ]
          },
          {
            "title": "How to cope",
            "subtitle": "Without pretending it's easy",
            "content": "A few strategies that feel realistic:",
            "bulletPoints": [
              "Pick a testing plan and protect it. If early testing makes you spiral, consider waiting for the clinic beta (or testing only once, close to the beta).",
              "Make two gentle \"after the call\" plans. One for a positive result, one for a negative result. Simple comforts count.",
              "Put boundaries on research. Forums can be supportive, but symptom-spotting threads can also amplify anxiety. Even a small limit helps.",
              "Name it for what it is. This is a high-stakes waiting period. Feeling edgy, emotional, or distracted is a common human response."
            ]
          }
        ],
        "references": [
          {
            "title": "ART Step-by-Step Guide (hormone support through pregnancy test).",
            "authors": "SART",
            "url": "https://www.sart.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "IVF Process Day by Day (clinic beta timing 9–14 days after transfer).",
            "authors": "CCRM Fertility",
            "url": "https://www.ccrmivf.com/",
            "evidenceLevel": "reference"
          },
          {
            "title": "Getting Through the Two-Week Wait (pregnancy test about two weeks after IUI or transfer).",
            "authors": "Shady Grove Fertility",
            "url": "https://www.shadygrovefertility.com/",
            "evidenceLevel": "reference"
          },
          {
            "title": "Medications for Inducing Ovulation (pregnancy tests may be falsely positive if done <10 days after hCG trigger).",
            "authors": "ASRM ReproductiveFacts",
            "url": "https://www.reproductivefacts.org/",
            "evidenceLevel": "reference"
          },
          {
            "title": "Progesterone Vaginal – Drug Information (common side effects).",
            "authors": "MedlinePlus",
            "url": "https://medlineplus.gov/",
            "evidenceLevel": "reference"
          }
        ]
      }
    },
    {
      "id": "new-article-preparing-for-art",
      "kind": "article",
      "tags": ["pre-cycle", "practical", "emotional"],
      "title": "Preparing for ART",
      "subtitle": "Getting ready for IUI, IVF, or egg freezing",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Starting fertility treatment is a big step. Whether you're heading toward IUI, IVF, or egg freezing, \"preparing\" isn't just about showing up for injections and blood tests. There are a few areas you can work on ahead of time so that when treatment starts, you feel more informed, organised, and supported.",
        "quickSummary": [
          {
            "title": "Check the medical basics.",
            "description": "Review general health, medications, vaccinations, and infection screening before treatment starts."
          },
          {
            "title": "Lifestyle nudges the odds.",
            "description": "Stopping smoking, reducing alcohol, managing weight, and improving sleep can improve outcomes and how you feel during treatment."
          },
          {
            "title": "Emotional prep matters.",
            "description": "Plan support in advance, agree on boundaries, and consider counselling if you live with anxiety, depression, or past trauma."
          }
        ],
        "sections": [
          {
            "title": "Check the medical basics",
            "subtitle": "Before treatment starts",
            "content": "A good pre-ART check-up often includes several areas. Ask your doctor: \"Are there any medical issues we should stabilize before starting?\" and \"Do I need any vaccines or infection screening before treatment?\"",
            "bulletPoints": [
              "General health review: blood pressure, BMI, chronic conditions (thyroid, diabetes, hypertension, etc.).",
              "Medications check: some drugs (for mood, blood pressure, acne, epilepsy, etc.) may need adjusting before pregnancy attempts. Don't stop anything by yourself. Ask the prescriber what's compatible with ART.",
              "Vaccination status: doctors often check immunity to rubella, varicella, and sometimes hepatitis before treatment, so they can update vaccines if needed.",
              "Infection screening: STI tests and blood tests (HIV, hepatitis, syphilis) are standard in many clinics before ART."
            ]
          },
          {
            "title": "Build a lifestyle foundation",
            "subtitle": "Supporting treatment",
            "content": "Lifestyle changes don't \"fix everything,\" but they can nudge the odds in your favour and improve how you feel during ART. You don't have to become a perfect wellness robot. Focus on 1-2 changes that feel achievable over a few months.",
            "bulletPoints": [
              "Smoking/vaping: Stopping smoking is one of the most powerful things you can do for fertility. It's linked to poorer egg and sperm quality, lower IVF success, and higher miscarriage risk.",
              "Alcohol: Heavy or frequent drinking is associated with lower fertility. Many clinics suggest minimising alcohol while preparing and stopping completely during stimulation.",
              "Weight and exercise: Very low or very high BMI can make it harder to conceive. Gentle, regular movement and balanced diet are more sustainable than \"crash\" changes.",
              "Sleep and stress: Poor sleep and chronic stress can worsen hormone balance and how you experience treatment. Simple routines like fixed wake times and relaxation exercises can help."
            ]
          },
          {
            "title": "Prepare emotionally",
            "subtitle": "This really matters",
            "content": "ART is rarely \"just a medical procedure.\" It touches identity, relationships, money, family expectations… and it's normal if it hits hard.",
            "bulletPoints": [
              "Decide what you want to know (or not know). Some people feel better with all the statistics; others prefer big-picture guidance.",
              "Plan support in advance. Is there a friend/partner who can come to key appointments? Does your clinic offer counselling or a support group?",
              "Agree on boundaries. Decide what you will/won't share with family, colleagues, or social media. It's okay to keep parts private.",
              "If you already live with anxiety, depression, past trauma, or chronic pain, check in with a therapist or mental-health professional before starting."
            ]
          },
          {
            "title": "Get the practical side under control",
            "subtitle": "Money, time, logistics",
            "content": "Planning ahead can avoid last-minute panic and reduce stress during treatment.",
            "bulletPoints": [
              "Money and insurance: Clarify what your insurance or national health system covers. Check if your clinic or employer offers financing programs.",
              "Work and scheduling: Ask how often you'll need to come in during a typical cycle and whether you'll need time off around retrieval/transfer.",
              "Think about transport, childcare, and how flexible your job is."
            ]
          },
          {
            "title": "Talk with your partner",
            "subtitle": "If you have one",
            "content": "If you're doing ART with a partner, you're both patients, even if only one of you has the injections. If you're solo, you might want a trusted \"fertility buddy\" who can share some of the emotional and practical load.",
            "bulletPoints": [
              "How far you're both willing to go: Number of cycles? Use of donor sperm/eggs/embryos? Adoption? Child-free life if treatments don't work?",
              "How to share the load: Who handles admin? Who gives injections? Who drives after procedures?",
              "Sex & intimacy: ART can make sex feel scheduled or medical. Naming this early helps you protect your relationship."
            ]
          },
          {
            "title": "Agree on your next step",
            "subtitle": "After this treatment",
            "content": "Before you start, it's helpful to ask: \"If this cycle doesn't work, what would you recommend next?\" You don't have to commit to that next step now, but knowing there is a plan can make a negative result feel slightly less like a dead end.",
            "bulletPoints": [
              "Try the same protocol again.",
              "Change stimulation or switch from IUI to IVF.",
              "Add or remove some lab options (ICSI, PGT, etc.).",
              "Consider donor gametes or surrogacy.",
              "Take a break."
            ]
          }
        ],
        "references": [
          {
            "title": "Fertility problems: assessment and management",
            "authors": "NICE",
            "journal": "Clinical guideline.",
            "url": "https://www.nice.org.uk/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Optimizing Natural Fertility; Infertility: An Overview (patient fact sheets).",
            "authors": "ASRM",
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "Diagnostic evaluation of the infertile female: a committee opinion",
            "authors": "ASRM Practice Committee",
            "journal": "Fertil Steril.",
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Diagnostic evaluation of the infertile male: a committee opinion",
            "authors": "ASRM Practice Committee",
            "journal": "Fertil Steril.",
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Impact of sleep patterns upon female neuroendocrinology and reproductive outcomes",
            "authors": "Beroukhim G, et al.",
            "journal": "Reprod Biol Endocrinol",
            "year": 2022,
            "url": "https://scholar.google.com/scholar?q=Impact%20of%20sleep%20patterns%20upon%20female%20neuroendocrinology%20and%20reproductive%20outcomes%20Beroukhim%20G%2C%20et%20al.%202022",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-egg-retrieval-day",
      "kind": "article",
      "tags": ["during-cycle", "ivf", "egg-freezing", "practical"],
      "title": "Egg retrieval day",
      "subtitle": "How to prepare, what happens, and how to recover",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Egg retrieval (also called egg collection or oocyte retrieval) is a short procedure, but it can feel like a big moment: it's the \"finish line\" of stimulation and the start of what comes next. This article walks you through how to get ready, what the day usually looks like, and what to expect afterward.",
        "quickSummary": [
          {
            "title": "Trigger timing is critical.",
            "description": "Your trigger injection, usually about 36 hours before retrieval, helps eggs reach final maturation. Don't guess if you miss it. Call your clinic."
          },
          {
            "title": "Plan for recovery.",
            "description": "Arrange a ride home and assume you won't drive for at least 24 hours. Most people feel \"period-ish\" rather than \"surgery-ish.\""
          },
          {
            "title": "Know warning signs.",
            "description": "Severe pain, marked bloating, fever, or passing much less urine than usual warrant an urgent call to your clinic."
          }
        ],
        "sections": [
          {
            "title": "What egg retrieval is",
            "subtitle": "In plain language",
            "content": "During IVF (or egg freezing), eggs are collected using a thin needle guided by a vaginal ultrasound. The doctor gently drains the fluid from each follicle; the embryology team then looks for eggs in that fluid.",
            "bulletPoints": [
              "The procedure is usually quick (often around 20 minutes) and typically done with sedation.",
              "Not every follicle contains an egg, and sometimes the number collected is lower than the number of follicles seen on ultrasound."
            ]
          },
          {
            "title": "How to prepare",
            "subtitle": "Before retrieval",
            "content": "Preparation is straightforward but important. Follow your clinic's specific instructions.",
            "bulletPoints": [
              "Trigger shot: Treat it as \"mission critical.\" You'll be told exactly when to take it. Timing matters for egg maturation.",
              "Fasting: Most clinics require no food or drink for several hours before retrieval because of sedation.",
              "The morning: Shower and avoid makeup, perfume, nail varnish, jewelry, and contact lenses.",
              "Arrange a ride home: Sedation affects judgement and coordination. Some clinics recommend car/taxi rather than public transport.",
              "Assume you won't drive or operate machinery for at least 24 hours."
            ]
          },
          {
            "title": "What happens on the day",
            "subtitle": "The procedure",
            "content": "After check-in, you'll change into a gown and a small IV line is placed for sedation.",
            "bulletPoints": [
              "An ultrasound probe goes into the vagina; a fine needle attached to it collects follicle fluid from each ovary.",
              "You're monitored throughout the procedure.",
              "Afterward, you usually rest in recovery for about 1-2 hours before going home."
            ]
          },
          {
            "title": "What to expect after",
            "subtitle": "Recovery symptoms",
            "content": "Most people feel \"period-ish\" rather than \"surgery-ish.\" Common, normal symptoms include:",
            "bulletPoints": [
              "Tiredness and mild cramping or pelvic discomfort.",
              "Light vaginal bleeding or blood-stained discharge (from where the needle passed through).",
              "Paracetamol/acetaminophen is commonly recommended for pain relief.",
              "Use pads, not tampons, to reduce infection risk.",
              "Plan for a quiet day: treat the first 24 hours as recovery time."
            ]
          },
          {
            "title": "When to call your clinic",
            "subtitle": "Warning signs",
            "content": "Call your clinic (or urgent care) if you have symptoms that feel \"more than expected\":",
            "bulletPoints": [
              "Persistent nausea or vomiting.",
              "Severe or worsening abdominal pain.",
              "Marked abdominal swelling or bloating.",
              "Passing much less urine than usual.",
              "Fever or feeling unwell.",
              "These can be warning signs of ovarian hyperstimulation syndrome (OHSS), which may worsen if pregnancy occurs."
            ]
          }
        ],
        "references": [
          {
            "title": "What to expect during egg retrieval. American Society for Reproductive Medicine.",
            "authors": "ASRM",
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "IVF - what happens. Human Fertilisation and Embryology Authority.",
            "authors": "HFEA",
            "url": "https://www.hfea.gov.uk/",
            "evidenceLevel": "official"
          }
        ]
      }
    },
    {
      "id": "new-article-bloating-after-retrieval",
      "kind": "article",
      "tags": ["post-cycle", "recovery", "practical"],
      "title": "Bloating after retrieval",
      "subtitle": "Why it happens and what you can do",
      "readTimeMinutes": 5,
      "body": {
        "introduction": "Egg retrieval is a key step in an IVF or egg-freezing cycle. During the procedure, a doctor uses a thin needle to collect mature eggs from your ovaries. It's usually quick and safe, but many people feel bloated, heavy, or \"full\" in the belly afterwards. That can be uncomfortable and a bit worrying if you weren't expecting it.",
        "quickSummary": [
          {
            "title": "It's very common.",
            "description": "Bloating after retrieval is linked to enlarged ovaries, temporary water retention, and mild pelvic irritation. For most people, it improves within a week."
          },
          {
            "title": "Simple steps help.",
            "description": "Gentle movement, adequate hydration, easy-to-digest foods, and clinic-approved pain relief can make you more comfortable."
          },
          {
            "title": "Know when to call.",
            "description": "Severe pain, rapid weight gain, very little urine, or shortness of breath warrant an urgent call to your clinic."
          }
        ],
        "sections": [
          {
            "title": "Why am I so bloated after retrieval?",
            "subtitle": "Understanding the causes",
            "content": "After egg retrieval, bloating is very common. Most of the time, it's due to a mix of factors.",
            "bulletPoints": [
              "Enlarged, stimulated ovaries: Before retrieval, hormones grow many follicles at once, making your ovaries temporarily bigger and pressing on nearby organs.",
              "Fluid shifts and water retention: High estrogen levels can make you retain more water, disturb your electrolyte balance, and slow digestion.",
              "Mild irritation after the procedure: The needle passes through the vaginal wall into each ovary, causing small spots of irritation that fade on their own.",
              "In some people who respond very strongly to stimulation, bloating can be an early sign of ovarian hyperstimulation syndrome (OHSS)."
            ]
          },
          {
            "title": "How long does the bloating last?",
            "subtitle": "What to expect",
            "content": "It varies a lot from person to person and depends on how many follicles you had, how high your estrogen levels were, and whether you're at risk of OHSS.",
            "bulletPoints": [
              "For many people, bloating and discomfort peak in the first 2-3 days after retrieval.",
              "Then gradually improve over about a week as hormone levels fall and your ovaries shrink back down.",
              "If you become pregnant in the same cycle, symptoms can sometimes last longer, because rising pregnancy hormones keep the ovaries active."
            ]
          },
          {
            "title": "Gentle movement",
            "subtitle": "Getting things moving",
            "content": "Short walks around your home or outside can stimulate digestion, reduce gas, and help fluid move.",
            "bulletPoints": [
              "Avoid high-impact exercise and heavy lifting until your clinic says it's safe.",
              "Your ovaries are still enlarged and more fragile right after retrieval."
            ]
          },
          {
            "title": "Hydration and food",
            "subtitle": "Being kind to your digestion",
            "content": "Aim for regular fluids through the day (often ~1.5–2 L unless your doctor advised otherwise). Water, herbal teas (like plain ginger or mint), and clear broths are good options.",
            "bulletPoints": [
              "Be cautious with \"detox\" or very strong laxative teas. They can cause diarrhea or dehydration and are not recommended if you might be pregnant.",
              "Prefer cooked, easy-to-digest foods: cooked vegetables, rice, oats, lean proteins.",
              "Reduce foods that often increase gas: large portions of beans/lentils, cruciferous vegetables (cabbage, broccoli, cauliflower), very greasy or ultra-processed foods.",
              "If you're sensitive to cow's milk, consider lactose-free or plant-based alternatives."
            ]
          },
          {
            "title": "Comfort and rest",
            "subtitle": "Supporting your recovery",
            "content": "Simple comfort measures can make a big difference, especially in the first 24-48 hours.",
            "bulletPoints": [
              "Soft, loose clothing around the waist can make a big difference in comfort.",
              "Rest when you need to, and use light heat (like a warm, not hot, water bottle wrapped in a cloth) if your clinic says it's okay.",
              "Many clinics recommend paracetamol/acetaminophen for pain. Always follow your clinic's advice on which medications are safe."
            ]
          },
          {
            "title": "When to call your clinic",
            "subtitle": "Warning signs",
            "content": "While bloating alone is common, you should contact your clinic promptly (or seek urgent medical care) if you notice any of the following. These can be signs of moderate or severe OHSS or another complication.",
            "bulletPoints": [
              "Your belly is very swollen and hard, getting noticeably bigger day by day.",
              "Severe pain that doesn't improve with rest or recommended pain medication.",
              "Shortness of breath, chest discomfort, or feeling like you can't lie flat comfortably.",
              "Nausea and vomiting that prevent you from drinking or eating.",
              "Very little urine (peeing much less than usual, very dark urine).",
              "Rapid weight gain over 24-48 hours (e.g., more than 2–3 kg / 4–6 lb).",
              "Dizziness, fainting, or feeling very unwell."
            ]
          }
        ],
        "references": [
          {
            "title": "Reproductive fact - OHSS.",
            "authors": "ASRM",
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "Prevention of moderate and severe ovarian hyperstimulation syndrome: a guideline.",
            "authors": "ASRM",
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "Patients with a history of hyperemesis gravidarum have similar symptoms during egg stimulation",
            "authors": "Fejzo MS, et al.",
            "journal": "Fertil Steril",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Patients%20with%20a%20history%20of%20hyperemesis%20gravidarum%20have%20similar%20symptoms%20during%20egg%20stimulation%20Fejzo%20MS%2C%20et%20al.%202010",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-egg-freezing-prep",
      "kind": "article",
      "tags": ["pre-cycle", "egg-freezing", "her", "practical"],
      "title": "Preparing to freeze your eggs",
      "subtitle": "Emotional, medical and financial preparation",
      "readTimeMinutes": 8,
      "body": {
        "introduction": "Freezing your eggs is a big act of self-care. It can also feel like… a lot. There's the decision itself, the hormones, the logistics, the cost, and all the \"what ifs\". This article is here to help you prepare, not to convince you either way. Think of it as a companion to the Egg Freezing Guide: the guide explains what it is, and this one focuses on how to get ready.",
        "quickSummary": [
          {
            "title": "Name your \"why\".",
            "description": "Understanding your personal reason for freezing eggs helps anchor you when the process feels heavy."
          },
          {
            "title": "Know the steps.",
            "description": "Stimulation phase, monitoring appointments, and retrieval: understanding the shape of the process makes it less scary."
          },
          {
            "title": "Plan the costs.",
            "description": "Cycle fees, medications, and annual storage add up. Knowing the numbers helps avoid surprises."
          }
        ],
        "sections": [
          {
            "title": "Emotional preparation",
            "subtitle": "Making space for your feelings",
            "content": "People rarely arrive at egg freezing from a neutral place. There's usually a story behind it: a demanding career, a breakup, health worries, the absence of a partner, or simply the sense that time is moving faster than you'd like.",
            "bulletPoints": [
              "\"I'm not ready to have a child now, but I don't want the decision to be taken away from me later\"",
              "\"I'm starting a treatment that could affect my fertility, and I want a safety net\"",
              "\"My path to parenthood feels uncertain, and this is one way to reduce the pressure\"",
              "It's normal if your emotions don't line up neatly: you might feel proud and proactive and sad or scared at the same time.",
              "Consider talking to a therapist familiar with fertility topics, a trusted friend or family member, or others who have frozen their eggs."
            ]
          },
          {
            "title": "Medical preparation",
            "subtitle": "Understanding the road ahead",
            "content": "Egg freezing can look intimidating from the outside: hormones, ultrasounds, an operating room, lab steps you never see. You don't need to become an expert, but understanding the shape of the process usually makes it less scary.",
            "bulletPoints": [
              "Most clinics start with a picture of your current situation: age, menstrual cycle pattern, hormone levels, and an ultrasound to look at your ovaries.",
              "Ask direct questions: \"What would be a realistic outcome from one cycle?\" \"Do you think I'll need one cycle or several?\"",
              "The stimulation phase lasts about 10-14 days with hormone injections and monitoring appointments.",
              "Think practically: workplace flexibility for morning appointments, someone to accompany you on retrieval day, avoiding big commitments during the cycle.",
              "Egg retrieval is a short procedure under sedation. You may feel tired, crampy or bloated afterwards for a couple of days."
            ]
          },
          {
            "title": "Financial preparation",
            "subtitle": "Making the numbers less scary",
            "content": "The cost of egg freezing is often one of the most stressful parts, especially because it's not just one number. It's a set of pieces that add up over time.",
            "bulletPoints": [
              "The cycle itself: consultations, monitoring, retrieval and freezing.",
              "Medications for stimulation can sometimes double the bill.",
              "Storage fees each year the eggs remain frozen.",
              "Using the eggs in the future (thawing, fertilising and transferring) has its own costs, similar to IVF.",
              "Explore: Does your insurance, country, or employer offer any coverage or fertility benefit? Are there payment plans or grants available?"
            ]
          },
          {
            "title": "Going at your own pace",
            "subtitle": "Permission to be human",
            "content": "Preparing for egg freezing isn't a test you pass or fail. It's a process of gathering information, listening to your body and emotions, and seeing what makes sense for you right now.",
            "bulletPoints": [
              "You're allowed to change your mind.",
              "You're allowed to ask the same question twice.",
              "You're allowed to feel fine one day and overwhelmed the next.",
              "You're allowed to decide to wait, or decide you're ready sooner than you thought.",
              "Taking time to think about the emotional, medical and financial sides means you're treating your fertility, and your future self, with care and respect."
            ]
          }
        ],
        "references": [
          {
            "title": "Mature oocyte cryopreservation: a guideline",
            "authors": "ASRM",
            "journal": "Fertility and Sterility",
            "year": 2013,
            "url": "https://www.asrm.org/",
            "evidenceLevel": "official"
          },
          {
            "title": "Oocyte vitrification as an efficient option for elective fertility preservation",
            "authors": "Cobo, A., et al.",
            "journal": "Fertility and Sterility",
            "year": 2016,
            "url": "https://scholar.google.com/scholar?q=Oocyte%20vitrification%20as%20an%20efficient%20option%20for%20elective%20fertility%20preservation%20Cobo%2C%20A.%2C%20et%20al.%202016",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-sperm-freezing-guide",
      "kind": "article",
      "tags": ["moon", "moon-guides", "pre-cycle", "sperm", "his", "practical"],
      "title": "Freezing your sperm",
      "subtitle": "Why, when, and how it works",
      "readTimeMinutes": 8,
      "body": {
        "introduction": "If having kids might be in your future (even if you're not sure when), sperm freezing can give you options. You store sperm now, in case life plans change or fertility becomes harder than expected. Think of it as a snapshot of your current fertility, kept on ice.",
        "quickSummary": [
          {
            "title": "A backup plan, not a treatment.",
            "description": "Freezing doesn't change your fertility today. It preserves a sample for later use through IUI, IVF, or ICSI."
          },
          {
            "title": "Common reasons to do it.",
            "description": "Flexibility, before treatments that can affect fertility (chemotherapy, radiation, testosterone, vasectomy), or because sperm quality can shift with age and lifestyle."
          },
          {
            "title": "Quality holds up well.",
            "description": "Live births have been reported after decades in storage, and frozen-vs-fresh outcomes in IVF/ICSI are often comparable."
          }
        ],
        "sections": [
          {
            "title": "What sperm freezing is (and isn't)",
            "subtitle": "A snapshot, not a treatment",
            "content": "Sperm freezing (also called sperm cryopreservation) means collecting a semen sample, preparing it in the lab, and storing it at ultra-low temperature in liquid nitrogen. That keeps it stable for the long term.",
            "bulletPoints": [
              "Freezing doesn't change your fertility today: it's storage, not treatment.",
              "Think of it as a snapshot of your current fertility potential.",
              "Frozen samples can be used later through IUI, IVF, or ICSI depending on the situation."
            ]
          },
          {
            "title": "Why people freeze sperm",
            "subtitle": "Flexibility, medical reasons, or hedging against time",
            "content": "There's rarely one reason. Some people want flexibility, others have a clear medical reason, and some are simply hedging against the fact that fertility isn't fixed.",
            "bulletPoints": [
              "Flexibility: career timing, travel, not having the right partner yet, peace of mind.",
              "Before medical treatment: chemotherapy, radiation, or testosterone therapy can affect sperm production, sometimes permanently.",
              "Before surgery: vasectomy is the common one. Banking samples first keeps biological children possible later.",
              "Because fertility can change: age, smoking, heavy alcohol, obesity, low physical activity, and some environmental exposures are all linked to reduced sperm quality.",
              "Sperm production is continuous: it takes roughly 2-3 months to make a 'new batch,' so lifestyle changes can matter if you're also planning to conceive."
            ]
          },
          {
            "title": "How it works, step by step",
            "subtitle": "Sample, lab, storage",
            "content": "The process is straightforward. Knowing the shape of it tends to make it less abstract.",
            "bulletPoints": [
              "You give a sample. Most clinics collect on-site in a private room. Some allow home collection if you can deliver it quickly. Rules vary.",
              "You'll usually be asked to abstain from ejaculation for a short window beforehand (typically 2-5 days). Your clinic will tell you what they recommend.",
              "Many people bank 2-3 samples across separate visits spaced a few days apart. That builds in redundancy and gives you more vials to work with later.",
              "The lab processes and freezes it. The sample is divided into multiple vials, so you may have more than one attempt available later.",
              "Some clinics do a 'test-thaw' on a small portion, which shows how the sample is likely to perform after freezing.",
              "Storage is long-term. Pregnancies and live births have been reported after decades in liquid-nitrogen storage."
            ]
          },
          {
            "title": "What it costs (US, ballpark)",
            "subtitle": "It's a bundle, not one number",
            "content": "Prices vary a lot by clinic, city, and what's included: consultation, infectious-disease screening, semen analysis, storage duration, and future shipping or retrieval fees.",
            "bulletPoints": [
              "Initial freezing plus the first year of storage typically ranges from a few hundred to a few thousand dollars, especially when bundled with required testing and admin fees.",
              "Ongoing storage is usually billed monthly or annually.",
              "Insurance rarely covers elective freezing. Coverage is more common before gonadotoxic treatment, so it's worth asking.",
              "When comparing quotes, ask: 'What exactly is included?' (testing, number of vials, first-year storage, future retrieval and shipping fees)."
            ]
          },
          {
            "title": "When you want to use it",
            "subtitle": "From storage to a path forward",
            "content": "When you're ready, the clinic thaws a vial and recommends the simplest option that fits your situation.",
            "bulletPoints": [
              "IUI (insemination): possible when the post-thaw sample is strong enough.",
              "IVF: may be recommended if there are other fertility factors in play.",
              "ICSI: a form of IVF used when count or motility is lower. It can work with very small numbers of motile sperm.",
              "Having frozen sperm doesn't lock you into IVF/ICSI, but it makes those options possible if you ever need them."
            ]
          },
          {
            "title": "Myth-busting",
            "subtitle": "Common worries, briefly",
            "content": "A few questions that come up a lot, with the short version of the answer.",
            "bulletPoints": [
              "Does freezing ruin sperm? It typically lowers motility, but many samples remain usable with modern lab methods.",
              "Is frozen worse than fresh? Not necessarily. In IVF/ICSI, outcomes can be comparable depending on the clinical context.",
              "Will this affect a future child's health? Sperm freezing has been used for decades and follow-up data are reassuring. Research continues to evolve, like much of fertility care.",
              "Do I lose fertility by freezing? No. Freezing doesn't take fertility away: it's storage for later."
            ]
          }
        ],
        "references": [
          {
            "title": "WHO laboratory manual for the examination and processing of human semen, 6th ed.",
            "authors": "World Health Organization",
            "journal": "WHO",
            "year": 2021,
            "url": "https://www.who.int/publications/i/item/9789240030787",
            "evidenceLevel": "official"
          },
          {
            "title": "Sexual and Reproductive Health Guidelines: Male Infertility",
            "authors": "European Association of Urology",
            "journal": "EAU Guidelines",
            "year": 2024,
            "url": "https://uroweb.org/guidelines/sexual-and-reproductive-health",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Diagnosis and Treatment of Infertility in Men",
            "authors": "American Urological Association",
            "journal": "AUA Guideline",
            "year": 2024,
            "url": "https://www.auanet.org/guidelines-and-quality/guidelines/male-infertility",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Fertility preservation in patients undergoing gonadotoxic therapy or gonadectomy: a committee opinion",
            "authors": "ASRM Practice Committee",
            "journal": "Fertility and Sterility",
            "year": 2019,
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/fertility-preservation-in-patients-undergoing-gonadotoxic-therapy-or-gonadectomy-a-committee-opinion-2019/",
            "evidenceLevel": "official"
          },
          {
            "title": "Fertility Preservation in People With Cancer: Guideline Update",
            "authors": "ASCO",
            "journal": "Journal of Clinical Oncology",
            "year": 2025,
            "url": "https://ascopubs.org/doi/10.1200/JCO.18.01914",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Effects of increased paternal age on sperm quality",
            "authors": "Sharma, R., et al.",
            "journal": "Reproductive Biology and Endocrinology",
            "year": 2015,
            "url": "https://scholar.google.com/scholar?q=Effects+of+increased+paternal+age+on+sperm+quality+Sharma+2015",
            "evidenceLevel": "review"
          },
          {
            "title": "Live births from frozen human semen stored for 40 years",
            "authors": "Szell, A. Z., et al.",
            "journal": "Journal of Assisted Reproduction and Genetics",
            "year": 2013,
            "url": "https://scholar.google.com/scholar?q=Live+births+from+frozen+human+semen+stored+for+40+years+Szell+2013",
            "evidenceLevel": "observational"
          },
          {
            "title": "Fresh versus frozen ejaculated sperm for intracytoplasmic sperm injection: outcomes comparable",
            "authors": "Punjani, N., et al.",
            "journal": "Fertility and Sterility",
            "year": 2022,
            "url": "https://scholar.google.com/scholar?q=Fresh+versus+frozen+ejaculated+sperm+ICSI+Punjani+2022",
            "evidenceLevel": "cohort"
          },
          {
            "title": "Follow-up studies of children born after frozen sperm donation",
            "authors": "Lansac, J., et al.",
            "journal": "Human Reproduction Update",
            "year": 2001,
            "url": "https://scholar.google.com/scholar?q=Follow-up+studies+of+children+born+after+frozen+sperm+Lansac+2001",
            "evidenceLevel": "cohort"
          },
          {
            "title": "Endocrine-disrupting chemicals and male reproductive health",
            "authors": "Skakkebæk, N. E., et al.",
            "journal": "Fertility and Sterility",
            "year": 2023,
            "url": "https://scholar.google.com/scholar?q=Endocrine-disrupting+chemicals+male+reproductive+health+2023",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-life-after-preservation",
      "kind": "article",
      "tags": ["post-cycle", "egg-freezing", "storage", "practical"],
      "title": "Life after preservation",
      "subtitle": "What happens now that everything is in storage",
      "readTimeMinutes": 8,
      "body": {
        "introduction": "Freezing eggs, sperm, or embryos is a big milestone. After the appointments, injections, and procedures are over, most people expect relief, and then realize a new question shows up: what happens now that everything is in storage?",
        "quickSummary": [
          {
            "title": "Storage pauses biology.",
            "description": "Samples in liquid nitrogen are in suspended animation, safe for years as long as the facility maintains proper conditions."
          },
          {
            "title": "Fees need active management.",
            "description": "Annual costs vary widely by clinic and city. Treat storage like a subscription you actively manage."
          },
          {
            "title": "Paperwork protects you.",
            "description": "Consent forms and disposition choices matter more than most people expect, especially for embryos."
          }
        ],
        "sections": [
          {
            "title": "What “storage” actually means",
            "subtitle": "Liquid nitrogen and long-term safety",
            "content": "When eggs, sperm, or embryos are frozen, they’re stored in cryogenic tanks filled with liquid nitrogen. At these temperatures, biological activity is essentially paused. That’s why storage can last for many years as long as the facility maintains proper conditions and monitoring.\n\nMost people store in one of two places. There’s no universal “best” option: what matters most is safety, tracking, and a clear plan for billing and consent.",
            "bulletPoints": [
              "🏥 Your fertility clinic’s lab, common if you froze as part of IVF or egg freezing at that clinic.",
              "🏢 A dedicated long-term storage facility, sometimes cheaper, sometimes chosen for logistics, sometimes recommended by clinics."
            ]
          },
          {
            "title": "Annual storage fees and what they include",
            "subtitle": "Costs, billing, and hidden add-ons",
            "content": "Storage pricing isn’t standardized in the US. It varies by clinic, city, and what you’re storing. Most facilities bill annually (most common), monthly, or offer multi-year discounts. Typical ranges: egg or embryo storage is often a few hundred to around $1,000/year; sperm storage is often a few hundred dollars per year.\n\nA helpful mindset is to treat storage like a subscription you actively manage. Most issues patients run into are administrative, not medical.",
            "bulletPoints": [
              "📌 Account or admin fees may apply on top of storage.",
              "⚠️ Late or reinstatement fees if a payment lapses.",
              "📦 Retrieval fees when you’re ready to use or move samples.",
              "🚚 Transfer and shipping costs if you move specimens to another facility later."
            ]
          },
          {
            "title": "The paperwork that matters more than people expect",
            "subtitle": "Consent, decisions, and disposition",
            "content": "After preservation, the most important admin task is making sure your storage agreements and consents match your life. It’s not the fun part, but it protects you.\n\nIf you only take one action after reading this article: download your signed consents and store them somewhere safe.",
            "bulletPoints": [
              "Who can make decisions: if you stored eggs or sperm, you generally control decisions. For embryos created with partner gametes, decisions are often shared unless legal paperwork says otherwise.",
              "What happens if you separate from a partner, if someone dies or loses capacity, if the clinic can’t reach you, or if fees go unpaid for a long time.",
              "Disposition options for embryos: continued storage, discarding, donation to research (where available), or donation to another person or family.",
              "Many people don’t want to decide disposition immediately, that’s normal. But it’s worth understanding what you signed."
            ]
          },
          {
            "title": "How to keep storage safe over time",
            "subtitle": "Questions you can ask without being an expert",
            "content": "You don’t need to know lab engineering to ask smart questions. If you’re choosing a storage site or reviewing your current one, look for these. If a facility can’t clearly explain tracking and monitoring, that’s a real signal to pause.",
            "bulletPoints": [
              "🔐 Clear tracking and identity checks: how do they prevent mix-ups and verify who can authorize changes?",
              "🔔 Continuous monitoring and alarms: how are tanks monitored, and what happens if an alarm triggers?",
              "✅ Quality standards and accreditation: many IVF/reproductive labs follow recognized accreditation frameworks and best-practice guidance for cryostorage."
            ]
          },
          {
            "title": "Can I move my samples later?",
            "subtitle": "Transfers between clinics or facilities",
            "content": "Yes. People transfer specimens all the time, moving states, changing clinics, choosing a different lab, or moving to lower-cost long-term storage. You’ll sign a release/transfer authorization, the receiving facility will give acceptance instructions, and shipment is done using specialized cryogenic transport.\n\nTransfers take coordination, so it’s best not to wait until you’re under time pressure (for example, right before a transfer cycle).",
            "bulletPoints": [
              "💰 Ask both facilities about total cost (paperwork + shipping).",
              "🔗 Confirm how they verify identity and chain of custody during the move.",
              "📅 Get realistic timelines from “request submitted” to “samples received.”"
            ]
          },
          {
            "title": "Planning your next steps",
            "subtitle": "What “using it” can look like",
            "content": "Preservation keeps options open. It doesn’t force one future path. A helpful reframe: the decision isn’t “Do I use them or not?” It’s often “When do I want this option available, and what would make me feel ready to try?”",
            "bulletPoints": [
              "Frozen sperm can later be used with IUI (depending on post-thaw quality) or IVF/ICSI (often preferred when vials are limited).",
              "Frozen eggs are later thawed, fertilized in the lab (ICSI is commonly used), grown into embryos, and transferred in a planned cycle.",
              "Frozen embryos: your next step is typically a frozen embryo transfer cycle when you’re ready."
            ]
          },
          {
            "title": "A simple annual check-in",
            "subtitle": "10 minutes, once a year",
            "content": "Once a year (often when the storage bill comes), do this quick review. This tiny routine prevents the most common storage headaches.",
            "bulletPoints": [
              "📞 Confirm the facility has your current email, phone, and address.",
              "💳 Confirm the billing cycle and what happens if a payment is missed.",
              "📜 Re-read your consent summary (especially if you stored embryos).",
              "💾 Save your receipt/invoice and the most recent consent copy.",
              "🔄 If your life changed (marriage, separation, move, health change), ask whether paperwork should be updated."
            ]
          },
          {
            "title": "When you might want extra support",
            "subtitle": "You don’t have to decide everything today",
            "content": "Decisions about long-term storage can get surprisingly emotional, especially for embryos. If you feel stuck, avoidant, or overwhelmed, you’re not alone. The goal is to keep your options safe, clear, and aligned with your life as it changes.",
            "bulletPoints": [
              "🧠 A brief session with a fertility-informed counselor.",
              "🏥 A clinic consult focused on family-building planning.",
              "💲 A financial counselor call if costs are building up and you want alternatives."
            ]
          }
        ],
        "references": [
          {
            "title": "Cryostorage of reproductive tissues in the IVF laboratory: a committee opinion",
            "authors": "ASRM Practice Committee",
            "year": 2020,
            "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Disposition of unclaimed embryos: an Ethics Committee opinion",
            "authors": "ASRM Ethics Committee",
            "year": 2021,
            "url": "https://www.asrm.org/practice-guidance/ethics-opinions/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Planned oocyte cryopreservation to preserve future reproductive potential",
            "authors": "ASRM Ethics Committee",
            "year": 2024,
            "url": "https://www.asrm.org/practice-guidance/ethics-opinions/",
            "evidenceLevel": "guideline"
          },
          {
            "title": "Reproductive Laboratory Accreditation Program (overview).",
            "authors": "College of American Pathologists (CAP)",
            "url": "https://www.cap.org/",
            "evidenceLevel": "guideline"
          }
        ]
      }
    },
    {
      "id": "new-article-diet-and-fertility",
      "kind": "article",
      "tags": ["lifestyle", "optimization", "nutritional"],
      "title": "Diet and fertility",
      "subtitle": "How diet may influence reproductive health",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "Diet, among numerous other factors, may influence reproductive health, with research indicating a link between diet and fertility. Indeed, certain foods and nutrients are believed to enhance reproductive health, while others may have a negative impact on fertility. This article aims to explain how diet can influence fertility and suggest nutritional choices that may enhance reproductive health.",
        "quickSummary": [
          {
            "title": "Mediterranean diet helps.",
            "description": "Following the Mediterranean diet may boost the likelihood of successful IVF by promoting reproductive health."
          },
          {
            "title": "Choose healthy fats.",
            "description": "Diets rich in unsaturated fats, whole grains, vegetables, and fish can improve fertility in both women and men."
          },
          {
            "title": "Limit processed foods.",
            "description": "Saturated fats and sugars may have negative impacts on fertility outcomes."
          }
        ],
        "sections": [
          {
            "title": "Healthy fats and whole grains",
            "subtitle": "The building blocks",
            "content": "Evidence suggests that diets rich in unsaturated fats, whole grains, vegetables, and fish can enhance fertility in both women and men. Conversely, saturated fats and sugars may be associated with less favorable fertility outcomes."
          },
          {
            "title": "Anti-inflammatory diets",
            "subtitle": "Why inflammation matters",
            "content": "Research indicates that anti-inflammatory diets, such as the Mediterranean diet, can improve fertility, increase the success rates of Assisted Reproductive Technology (ART), and increase sperm quality in men. These diets favor the consumption of monounsaturated and n-3 polyunsaturated fatty acids, flavonoids, and limit the intake of red and processed meat."
          },
          {
            "title": "The Mediterranean diet",
            "subtitle": "A potential path to fertility",
            "content": "This diet offers numerous health benefits, including better heart health and weight management. Moreover, research suggests it may positively impact fertility due to its anti-inflammatory and antioxidant-rich properties, which could benefit both male and female reproductive health. It's not just a diet, but a lifestyle that also promotes physical activity and social meal times."
          },
          {
            "title": "Key components of the Mediterranean diet",
            "subtitle": "What to eat",
            "content": "Here are the key components of this diet:",
            "bulletPoints": [
              "High intake of fruits, vegetables, and whole grains.",
              "Healthy fats mainly from olive oil, nuts, and seeds.",
              "Moderate consumption of fish, seafood, poultry, and dairy.",
              "Limited intake of red meat and processed foods.",
              "Use of herbs and spices for flavor, reducing salt intake.",
              "Optional, moderate consumption of red wine."
            ]
          },
          {
            "title": "Making informed choices",
            "subtitle": "Your path forward",
            "content": "The path to parenthood may sometimes require making dietary choices that support fertility. Understanding the connection between diet and fertility can empower individuals and couples to make informed nutritional decisions. Whether it's considering a supplement like folic acid, or trying a Mediterranean or anti-inflammatory diet, these dietary approaches could be part of a comprehensive strategy to enhance fertility. Seeking advice from healthcare professionals for personalized dietary guidance can further support individuals on this significant life journey."
          }
        ],
        "references": [
          {
            "title": "Diet and fertility: a review. American Journal of Obstetrics and Gynecology",
            "authors": "Gaskins, A.J., Chavarro, J.E.",
            "year": 2018,
            "url": "https://scholar.google.com/scholar?q=Diet%20and%20fertility%3A%20a%20review.%20American%20Journal%20of%20Obstetrics%20and%20Gynecology%20Gaskins%2C%20A.J.%2C%20Chavarro%2C%20J.E.%202018",
            "evidenceLevel": "review"
          },
          {
            "title": "The Influence of Diet on Fertility and the Implications for Public Health Nutrition in the United States",
            "authors": "Panth, N., Gavarkovs, A. et al.",
            "journal": "Frontiers in Public Health",
            "year": 2018,
            "url": "https://scholar.google.com/scholar?q=The%20Influence%20of%20Diet%20on%20Fertility%20and%20the%20Implications%20for%20Public%20Health%20Nutrition%20in%20the%20United%20States%20Panth%2C%20N.%2C%20Gavarkovs%2C%20A.%20et%20al.%202018",
            "evidenceLevel": "review"
          },
          {
            "title": "Anti-Inflammatory Diets in Fertility: An Evidence Review",
            "authors": "Alesi, S., Villani, A. et al.",
            "journal": "Nutrients",
            "year": 2022,
            "url": "https://scholar.google.com/scholar?q=Anti-Inflammatory%20Diets%20in%20Fertility%3A%20An%20Evidence%20Review%20Alesi%2C%20S.%2C%20Villani%2C%20A.%20et%20al.%202022",
            "evidenceLevel": "review"
          },
          {
            "title": "Mediterranean diet and infertility: a systematic review with meta-analysis of cohort studies",
            "authors": "Muffone, A.R.M.C. et al.",
            "journal": "Nutrition Reviews",
            "year": 2023,
            "url": "https://scholar.google.com/scholar?q=Mediterranean%20diet%20and%20infertility%3A%20a%20systematic%20review%20with%20meta-analysis%20of%20cohort%20studies%20Muffone%2C%20A.R.M.C.%20et%20al.%202023",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-temperature-and-fertility",
      "kind": "article",
      "tags": ["lifestyle", "his", "sperm"],
      "title": "Heat, temperature, and male fertility",
      "subtitle": "How heat affects sperm and what you can do",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Most people don't think about temperature as a fertility factor, until they hear it from a clinician: sperm production works best when the testicles stay a little cooler than the rest of the body. That's not a fun fact; it's a real part of male biology. When heat exposure is frequent or intense enough (from lifestyle, work, illness, or environment), it can temporarily lower sperm quality and, in some cases, make it harder to conceive.",
        "quickSummary": [
          {
            "title": "Cooling matters.",
            "description": "Sperm production works best when testicles stay a few degrees cooler than core body temperature."
          },
          {
            "title": "Effects are reversible.",
            "description": "Heat-related sperm changes often recover after reducing exposure, but improvements take 2-3 months to appear."
          },
          {
            "title": "Simple changes help.",
            "description": "Avoiding hot tubs, keeping laptops off your lap, and choosing breathable underwear can make a difference."
          }
        ],
        "sections": [
          {
            "title": "Why temperature matters for sperm",
            "subtitle": "The biology of spermatogenesis",
            "content": "Sperm are made in the testicles through a process called spermatogenesis. For that process to run well, the testes typically need to stay a few degrees cooler than core body temperature. That's why the testes sit outside the body and why the scrotum has built-in \"cooling\" mechanisms.",
            "bulletPoints": [
              "When the testicles spend too much time warmer than their comfort zone, sperm-making cells can become stressed.",
              "Studies link heat stress to lower sperm concentration and total count.",
              "Heat can reduce motility (how well sperm move) and affect morphology (shape).",
              "In some settings, heat increases markers of sperm DNA/packaging stress.",
              "Importantly: heat effects are often reversible, but they can take time."
            ]
          },
          {
            "title": "The timeline that helps everything make sense",
            "subtitle": "Why changes take months",
            "content": "Sperm take about 2-3 months to develop and mature. That means:",
            "bulletPoints": [
              "Heat exposure today may show up in semen results weeks later.",
              "If you reduce heat exposure now, improvements (if they happen) often show up after a couple of months, not a couple of days.",
              "This timeline is also why clinicians often repeat semen testing after ~3 months when you make lifestyle changes."
            ]
          },
          {
            "title": "What heat exposure actually looks like",
            "subtitle": "Real-life examples",
            "content": "Most everyday warmth isn't a problem: your body is good at regulating scrotal temperature. The issues are usually repeated, prolonged, or high-intensity heat.",
            "bulletPoints": [
              "Hot tubs, hot baths, and frequent sauna: Regular high-heat exposure can raise scrotal temperature enough to affect sperm parameters.",
              "Fever and illness: A high fever is a form of whole-body heat stress that can temporarily affect sperm quality weeks later.",
              "Work heat and protective gear: Jobs with sustained heat exposure can increase infertility risk. Heavy protective clothing can make cooling harder.",
              "Everyday heat traps: Long periods sitting with thighs closed, laptop directly on the lap, or very tight underwear/pants that reduce airflow."
            ]
          },
          {
            "title": "How much does heat affect fertility?",
            "subtitle": "The real-world impact",
            "content": "The honest answer: it varies. Large bodies of research suggest that higher ambient temperatures and heat waves are associated with worse semen parameters at the population level.",
            "bulletPoints": [
              "If you're already close to the lower end of normal sperm parameters, heat may \"push\" things into a range where conception is harder.",
              "If your baseline semen parameters are strong, heat may not change your ability to conceive, but it can still reduce margins.",
              "The overall research direction is consistent enough that temperature is now considered a meaningful environmental factor for male reproductive health."
            ]
          },
          {
            "title": "What you can do",
            "subtitle": "Simple, practical steps",
            "content": "If you're trying to conceive (or preparing for fertility testing/treatment), these steps are reasonable and low-risk:",
            "bulletPoints": [
              "Pause hot tubs, very hot baths, and sauna for a few months while you're actively trying.",
              "If you work in heat, ask what's realistic: more breaks, hydration, cooling options, or changes in protective gear when safe.",
              "Keep laptops off the lap (use a desk or lap desk).",
              "Choose underwear/pants that don't trap heat (comfort + airflow).",
              "If you recently had a high fever, consider that it may affect semen results for a while. Your clinician may suggest retesting later."
            ]
          },
          {
            "title": "Does temperature affect fertility in women too?",
            "subtitle": "Broader research",
            "content": "There's growing research on temperature and reproduction more broadly. Some large studies link extreme heat (and sometimes cold) to changes in couple fecundability (time to pregnancy) and to outcomes in assisted reproduction, though the evidence is more variable and the mechanisms differ from male heat effects."
          },
          {
            "title": "When to talk to a clinician",
            "subtitle": "Signs to check in",
            "content": "Consider checking in if:",
            "bulletPoints": [
              "You've been trying for the recommended time window for your age and situation.",
              "You have known high heat exposure (work, frequent sauna/hot tubs).",
              "You have symptoms like testicular pain/swelling (which can signal issues that need evaluation).",
              "You're about to start ART and want to optimize semen quality.",
              "A clinician can also help interpret whether heat is likely a major contributor, or just one small piece."
            ]
          }
        ],
        "references": [
          {
            "title": "The Impact of High Ambient Temperature on Human Sperm Parameters: A Meta-Analysis. Iran J Public Health. 2022.",
            "authors": "Hoang-Thi AP, et al.",
            "url": "https://scholar.google.com/scholar?q=The%20Impact%20of%20High%20Ambient%20Temperature%20on%20Human%20Sperm%20Parameters%3A%20A%20Meta-Analysis.%20Iran%20J%20Public%20Health.%202022.%20Hoang-Thi%20AP%2C%20et%20al.",
            "evidenceLevel": "review"
          },
          {
            "title": "Seminal and molecular evidence that sauna exposure affects human spermatogenesis. Human Reproduction. 2013.",
            "authors": "Garolla A, et al.",
            "url": "https://scholar.google.com/scholar?q=Seminal%20and%20molecular%20evidence%20that%20sauna%20exposure%20affects%20human%20spermatogenesis.%20Human%20Reproduction.%202013.%20Garolla%20A%2C%20et%20al.",
            "evidenceLevel": "review"
          },
          {
            "title": "Wet heat exposure: a potentially reversible cause of low semen quality in infertile men. Int Braz J Urol. 2007.",
            "authors": "Shefi S, et al.",
            "url": "https://scholar.google.com/scholar?q=Wet%20heat%20exposure%3A%20a%20potentially%20reversible%20cause%20of%20low%20semen%20quality%20in%20infertile%20men.%20Int%20Braz%20J%20Urol.%202007.%20Shefi%20S%2C%20et%20al.",
            "evidenceLevel": "review"
          },
          {
            "title": "Causes, effects and molecular mechanisms of testicular heat stress. Reprod Biomed Online. 2015.",
            "authors": "Durairajanayagam D.",
            "url": "https://scholar.google.com/scholar?q=Causes%2C%20effects%20and%20molecular%20mechanisms%20of%20testicular%20heat%20stress.%20Reprod%20Biomed%20Online.%202015.%20Durairajanayagam%20D.",
            "evidenceLevel": "review"
          },
          {
            "title": "The relation between daily activities and scrotal temperature. Hum Reprod. 2002.",
            "authors": "Hjollund NHI, et al.",
            "url": "https://scholar.google.com/scholar?q=The%20relation%20between%20daily%20activities%20and%20scrotal%20temperature.%20Hum%20Reprod.%202002.%20Hjollund%20NHI%2C%20et%20al.",
            "evidenceLevel": "review"
          }
        ]
      }
    },
    {
      "id": "new-article-stis-and-fertility",
      "kind": "article",
      "tags": ["lifestyle", "both-partners", "testing"],
      "title": "STIs and fertility",
      "subtitle": "What to know about infections and trying to conceive",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "STIs (sexually transmitted infections) are common, and many cause no symptoms, which is why they can affect fertility without you realizing it. The good news: most STIs are treatable, and taking action early can protect your future options.",
        "quickSummary": [
          {
            "title": "Silent impact.",
            "description": "Many STIs cause no symptoms but can still affect fertility if left untreated."
          },
          {
            "title": "Inflammation is key.",
            "description": "Most fertility-related STI problems come from inflammation and scarring in the reproductive tract."
          },
          {
            "title": "Testing protects you.",
            "description": "Since symptoms are often absent, regular testing is the best way to catch and treat infections early."
          }
        ],
        "sections": [
          {
            "title": "The big picture: how STIs can affect fertility",
            "subtitle": "Understanding the connection",
            "content": "Most fertility-related STI problems come from inflammation.",
            "bulletPoints": [
              "In people with a uterus, certain infections can spread upward from the cervix into the uterus and fallopian tubes, causing pelvic inflammatory disease (PID).",
              "PID can lead to scarring in or around the fallopian tubes, which may make it harder to conceive and can increase the risk of an ectopic pregnancy.",
              "In people with testes, infections can inflame parts of the reproductive tract such as the epididymis (the structure that stores and transports sperm).",
              "Epididymitis is usually treatable, but if it's severe or recurrent, inflammation and scarring can sometimes contribute to fertility problems.",
              "The key message: STIs can affect fertility mostly when they're untreated long enough to cause deeper inflammation or scarring."
            ]
          },
          {
            "title": "Which STIs matter most for fertility",
            "subtitle": "The ones to know about",
            "content": "If your goal is to protect fertility, these infections are worth knowing, not to scare you, but to focus attention on the ones where early treatment matters most.",
            "bulletPoints": [
              "Chlamydia is a top concern because it is very common and frequently causes no symptoms. In women, untreated chlamydia can contribute to PID and tubal scarring. In men, it can cause urethritis and epididymitis.",
              "Gonorrhea can follow a similar pattern. It can be symptom-free, but it can also cause more intense inflammation leading to PID in women and urethritis/epididymitis in men.",
              "Mycoplasma genitalium (Mgen) is less well-known and not always included on basic STI panels. It has been associated with cervicitis and PID and fertility-related complications in women.",
              "Trichomoniasis (\"trich\") is common and treatable. It can cause genital inflammation and is associated with adverse pregnancy outcomes like preterm delivery."
            ]
          },
          {
            "title": "Viral and systemic infections",
            "subtitle": "Important for pregnancy planning",
            "content": "These infections matter for pregnancy planning even if they don't usually cause infertility through scarring:",
            "bulletPoints": [
              "HPV typically doesn't cause infertility directly, but HPV-related cervical disease and some treatments for it can affect pregnancy planning. HPV vaccination helps prevent cancers and precancers.",
              "Herpes (HSV) doesn't usually prevent conception, but it matters in pregnancy because infection near delivery can pose risks to a newborn.",
              "Syphilis is critical to identify and treat in pregnancy because it can cause serious outcomes for the baby if untreated.",
              "HIV doesn't automatically prevent pregnancy. With effective treatment and viral suppression, pregnancy can be very safe and the risk of transmission to a baby can be extremely low."
            ]
          },
          {
            "title": "\"But I feel fine.\" Why testing matters",
            "subtitle": "The hidden nature of STIs",
            "content": "Many people assume they would \"know\" if they had an STI. Unfortunately, that's often not true, especially with chlamydia. You can feel completely normal while an infection quietly causes inflammation.",
            "bulletPoints": [
              "That's why screening guidance exists: testing catches infections before they cause lasting damage.",
              "In the U.S., routine screening for chlamydia and gonorrhea is strongly recommended for sexually active women under 25 and others at increased risk.",
              "Testing is simple, often just a urine sample or swab, and treatment is usually straightforward antibiotics."
            ]
          },
          {
            "title": "If you're trying to conceive",
            "subtitle": "A practical approach",
            "content": "If pregnancy is on your mind, a calm, practical approach is best:",
            "bulletPoints": [
              "Start with testing early, especially if you haven't been screened recently or you've had new or multiple partners.",
              "If a test is positive, treat promptly and make sure partners are treated too. Reinfection is common when only one person is treated.",
              "Many guidelines recommend retesting a few months after treatment for chlamydia or gonorrhea, because repeat infection can happen even when treatment worked.",
              "If you've ever been diagnosed with PID, had recurring STIs, experienced pelvic pain, or had an ectopic pregnancy, tell your clinician. Those details can change which fertility tests they prioritize."
            ]
          },
          {
            "title": "When to seek care urgently",
            "subtitle": "Warning signs",
            "content": "Seek medical attention promptly if you experience:",
            "bulletPoints": [
              "Severe pelvic or lower abdominal pain.",
              "Fever with pelvic symptoms.",
              "Unusual discharge with pain or fever.",
              "Pain during sex combined with other symptoms.",
              "These could indicate PID or another infection that needs prompt treatment."
            ]
          }
        ],
        "references": [
          {
            "title": "Sexually Transmitted Infections Treatment Guidelines",
            "authors": "CDC",
            "year": 2021,
            "url": "https://www.cdc.gov/",
            "evidenceLevel": "official"
          },
          {
            "title": "Risk of sequelae after Chlamydia trachomatis genital infection in women",
            "authors": "Haggerty CL, et al.",
            "journal": "J Infect Dis",
            "year": 2010,
            "url": "https://scholar.google.com/scholar?q=Risk%20of%20sequelae%20after%20Chlamydia%20trachomatis%20genital%20infection%20in%20women%20Haggerty%20CL%2C%20et%20al.%202010",
            "evidenceLevel": "review"
          },
          {
            "title": "Subclinical pelvic inflammatory disease and infertility",
            "authors": "Wiesenfeld HC, et al.",
            "journal": "Obstet Gynecol",
            "year": 2012,
            "url": "https://scholar.google.com/scholar?q=Subclinical%20pelvic%20inflammatory%20disease%20and%20infertility%20Wiesenfeld%20HC%2C%20et%20al.%202012",
            "evidenceLevel": "review"
          },
          {
            "title": "Do male infections play a role in infertility? Andrology",
            "authors": "Lis R, et al.",
            "year": 2015,
            "url": "https://scholar.google.com/scholar?q=Do%20male%20infections%20play%20a%20role%20in%20infertility%3F%20Andrology%20Lis%20R%2C%20et%20al.%202015",
            "evidenceLevel": "review"
          }
        ]
      }
    }
  ],
  "relationships": [],
  "localeVariants": []
}
